import { EventEmitter } from '@angular/core';
import { BehaviorSubject, merge, Observable, Subject } from 'rxjs';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';
import { StringPropertyViewModel } from './base-type/string-property-view-model';
import { BaseViewModel } from './base-view-model';
import { PropertyViewModel } from './property-view-model';
import { PropertyViewModelInitializationInfo } from './property-view-model-initialization-info';
import { ViewModelEventDispatcher } from './view-model-event-dispatcher';
import { BoolMetaData, DateTimeMetaData, EnumMetaData, MetaDataUtils, ModelVersionMetaData, StringMetaData } from '../meta-data';
import { MessageContainer } from './message-container';
import { NumericPropertyViewModel } from './base-type/numeric-property-view-model';
import { NNumericPropertyViewModel } from './base-type/nnumeric-property-view-model';
import { EnumPropertyViewModel, NEnumPropertyViewModel } from './base-type/enum-property-view-model';
import { BoolPropertyViewModel, NBoolPropertyViewModel } from './base-type/bool-property-view-model';
import { DateTimePropertyViewModel, NDateTimePropertyViewModel } from './base-type/date-time-property-view-model';
import { DateTimeOffsetPropertyViewModel, NDateTimeOffsetPropertyViewModel } from './base-type/date-time-offset-property-view-model';
import { MessageResourceManager } from '../resources/message-resource-manager';
import { NumericMetaData } from '../meta-data/numeric-meta-data';
import { GenericsPropertiesTypeInspector, GenericsPropertiesTypeInterface } from '../decorators/generics-properties-type.decorator';
import { VersionPropertyViewModel } from './base-type/version-property-view-model';
import { PropertyViewModelFactory } from './property-view-model-factory';
import { GuidPropertyViewModel } from './base-type/guid-property-view-model';
import { LogService } from '@nts/std/utility';
import { NTimeSpanPropertyViewModel, TimeSpanPropertyViewModel } from './base-type/time-span-property-view-model';
import { CustomPropertyViewModelInspector } from './decorators/custom-property-view-model.decorator';
import { ClassConstructor } from '@nts/std/serialization';
import { BigNumber, ClassInformationType, CloneTypes, DomainModelExtension, DomainModelState, GenericsPropertiesType, SourceMessage, TimeSpan, ViewModelStates } from '@nts/std/types';
import { DateTimeOffset } from '@nts/std/types';
import type { AggregateMetaDataInterface, BaseErrorInterface, BaseViewModelInterface, BoolMetaDataInterface, ClassInformationInterface, CollectionViewModelInterface, CoreOrchestratorViewModelInterface, DateTimeMetaDataInterface, DomainModelMetaDataInterface, ExtensionDataViewModelArguments, ExtensionDataViewModelInterface, EnumMetaDataInterface, ExternalDomainModelChangedEventArgsInterface, ExternalRetrieverInterface, ExternalViewModelInterface, GetGenericPropertyViewModelInterface, GetPropertyViewModelInterface, GuidMetaDataInterface, InternalViewModelInterface, ModelChangedEventArgsInterface, ModelVersionMetaDataInterface, ModifiedSubscriberInterface, NumericMetaDataInterface, NumericPropertyViewModelInterface, PropertyMetaDataInterface, PropertyViewModelInterface, PropertyViewModelPropertyChangedEventArgsInterface, ReservedNamePathInterface, StringMetaDataInterface, StringPropertyViewModelInterface, TimeSpanMetaDataInterface, ViewModelEventDispatcherInterface, ViewModelInterface, VersionPropertyViewModelInterface, ExternalMetaDataInterface } from '@nts/std/interfaces';
import type { IdentityInterface, ModelInterface } from '@nts/std/interfaces';
import { PropertyViewModelPropertyChangedEventArgs } from './property-view-property-changed-event-args';
import { DynamicExternal } from '../domain-models/dynamic-external';
import { ViewModelFactory } from './view-model-factory';
import { DynamicInternal } from '../domain-models/dynamic-internal';
import { DynamicInternalIdentity } from '../domain-models/dynamic-internal.identity';
import { CollectionViewModel } from './collection-view-model';
import { DynamicExternalIdentity } from '../domain-models/dynamic-external.identity';

export abstract class ViewModel<
  TModel extends ModelInterface<TIdentity>,
  TIdentity extends IdentityInterface
> extends BaseViewModel implements ViewModelInterface<TIdentity, TModel>, GenericsPropertiesTypeInterface {

  #skipValidation: boolean = false;
  #postInitFunctions: (() => Promise<any>)[] = [];

  private _externalViewModelsMap: Map<string, ExternalViewModelInterface> = new Map();
  private _externalViewModelsDirty = true;
  private _internalViewModelsMap: Map<string, InternalViewModelInterface> = new Map();
  private _internalViewModelsDirty = true;
  private _collectionViewModelsMap: Map<string, CollectionViewModelInterface> = new Map();
  private _collectionViewModelsDirty = true;
  private internalExtension: ExtensionDataViewModelInterface<TIdentity, TModel>;
  private _domainModel: TModel;
  private _isRemoving = false;
  private readonly _genericsPropertiesType: GenericsPropertiesType<any>;

  // Variabile utilizzate per la cache in memoria dei pvm
  protected behindPropertyViewModels: Map<string, PropertyViewModelInterface> = new Map<string, PropertyViewModelInterface>();

  // Necessario per notificare che ci sono stati cambiamenti sul viewmodel
  protected _modifiedSubscriber: ModifiedSubscriberInterface;
  protected _layoutDefaultValueList: Map<string, string | number | BigNumber | boolean | Date | DateTimeOffset>;

  domainModelName: string;
  domainModelType: ClassConstructor<TModel>;
  domainModelFullName: string;
  protected internalMetadataDescription: string = '';
  metadataDescription$: BehaviorSubject<string> = new BehaviorSubject<string>(this.internalMetadataDescription);
  protected internalMetadataShortDescription: string = '';
  metadataShortDescription$: BehaviorSubject<string> = new BehaviorSubject<string>(this.internalMetadataShortDescription);
  relationViewModels: Map<string, BaseViewModelInterface & ReservedNamePathInterface>;
  externalRetriever: ExternalRetrieverInterface;
  domainModelMetaData: DomainModelMetaDataInterface;
  isMock = false;
  reservedPath = '';
  reservedName = '';
  propertyViewModels: Map<string, PropertyViewModelInterface> = new Map<string, PropertyViewModelInterface>();
  aggregateMetaData: AggregateMetaDataInterface;
  propertyChanged: EventEmitter<PropertyViewModelPropertyChangedEventArgsInterface<any>> = new EventEmitter<PropertyViewModelPropertyChangedEventArgsInterface<any>>();

  /**
   * EventEmitter utilizzato per notificare i cambiamenti del viewModel, come parametro utilizza una stringa che
   * identifica il tipo di cambiamento avvenuto (es. "viewModelChanged:<propertyName>").
   */
  viewModelChanged = new EventEmitter<string>();

  /**.
   * Subject utilizzato per debouncere gli eventi di viewModelChanged, come parametro utilizza una stringa che
   * identifica il tipo di cambiamento avvenuto (es. "propertyChanged:<propertyName>")
   */
  viewModelChangeDebouncer: Subject<string> = new Subject<string>();
  eventDispatcher: ViewModelEventDispatcherInterface;

  get externalViewModels(): Map<string, ExternalViewModelInterface> {
    if (this._externalViewModelsDirty) {
      this._externalViewModelsMap = new Map();
      for (const [key, value] of this.relationViewModels) {
        // Esempio di filtro: solo quelli con reservedName non vuoto
        if ((value as ClassInformationInterface)?.classType === ClassInformationType.ExternalViewModel) {
          this._externalViewModelsMap.set(key, value as ExternalViewModelInterface);
        }
      }
      this._externalViewModelsDirty = false;
    }
    return this._externalViewModelsMap;
  }

  get internalViewModels(): Map<string, InternalViewModelInterface> {
    if (this._internalViewModelsDirty) {
      this._internalViewModelsMap = new Map();
      for (const [key, value] of this.relationViewModels) {
        // Esempio di filtro: solo quelli con reservedName non vuoto
        if ((value as ClassInformationInterface)?.classType === ClassInformationType.InternalViewModel) {
          this._internalViewModelsMap.set(key, value as InternalViewModelInterface);
        }
      }
      this._internalViewModelsDirty = false;
    }
    return this._internalViewModelsMap;
  }

  get collectionViewModels(): Map<string, CollectionViewModelInterface> {
    if (this._collectionViewModelsDirty) {
      this._collectionViewModelsMap = new Map();
      for (const [key, value] of this.relationViewModels) {
        // Esempio di filtro: solo quelli con reservedName non vuoto
        if (value instanceof CollectionViewModel) {
          this._collectionViewModelsMap.set(key, value as CollectionViewModelInterface);
        }
      }
      this._collectionViewModelsDirty = false;
    }
    return this._collectionViewModelsMap;
  }

  /**
   * Passare la lista dei metadati custom csi vuole implementare delle collection view model custom
   * @returns lista meta dati custom della collection
   */
  getCustomCollectionViewModelsMetaData() {
    return [];
  }

  get extension(): ExtensionDataViewModelInterface<TIdentity, TModel> {
    if (this.internalExtension == null) {
      this.internalExtension = new ExtensionDataViewModel<TIdentity, TModel>({
        domainModel: this.domainModel,
        domainModelMetaData: this.domainModelMetaData,
        modifiedSubscriber: this.modifiedSubscriber,
        parent: this as ViewModelInterface,
        eventDispatcher: this.eventDispatcher,
        aggregateMetaData: this.aggregateMetaData,
        reservedPath: this.reservedPath,
        reservedName: this.reservedName,
      });
    }
    return this.internalExtension;
  }

  // Da valutare, proprità che indica se un oggetto della collection e in fase di rimozione
  get isRemoving() {
    return this._isRemoving;
  }

  get metadataDescription(): string {
    return this.internalMetadataDescription;
  }

  set metadataDescription(value) {
    if (this.internalMetadataDescription !== value) {
      this.internalMetadataDescription = value;
      this.onPropertyChanged('metadataDescription');
      this.metadataDescription$.next(value);
    }
  }

  override get skipValidation(): boolean {
    return this.#skipValidation;
  }

  override set skipValidation(value) {
    if (this.#skipValidation !== value) {
      this.#skipValidation = value;

      if (this.domainModel != null) {
        this.domainModel.skipValidation = value;
      }

      this.onPropertyChanged('skipValidation');

      this.skipValidation$.next(value);
    }
  }

  get metadataShortDescription(): string {
    return this.internalMetadataShortDescription;
  }

  set metadataShortDescription(value) {
    if (this.internalMetadataShortDescription !== value) {
      this.internalMetadataShortDescription = value;
      this.onPropertyChanged('metadataShortDescription');
      this.metadataShortDescription$.next(value);
    }
  }

  override set isEnabled(value) {
    this._isEnabled = value;
    this.switchIsEnabledForPropertyViewModels(value);
    this.switchIsEnabledForInternalExternalCollectionViewModels(value, true, true, true);
    this.onPropertyChanged('isEnabled');
    this.isEnabled$.next(value);
  }

  override get isEnabled(): boolean {
    return this._isEnabled;
    // TODO verificare se si può ottimizzare così e magari eliminare i cicli sotto
    // return this.parent ? this.parent.isEnabled && this._isEnabled : this._isEnabled;
  }

  override set isVisible(value) {
    this._isVisible = value;
    this.switchIsVisibleForPropertyViewModels(value);
    this.switchIsVisibleForInternalExternalCollectionViewModels(value, true, true, true);
    this.onPropertyChanged('isVisible');
    this.isVisible$.next(value);
  }

  override get isVisible() {
    return this._isVisible;
    // TODO verificare se si può ottimizzare così e magari eliminare i cicli sotto
    // return this.parent ? this.parent.isVisible && this._isVisible : this._isVisible;
  }

  switchIsVisibleForPropertyViewModels(isVisible: boolean) {
    this.propertyViewModels.forEach((value, key) => {
      value.isVisible = isVisible;
    });
  }

  switchIsVisibleForInternalExternalCollectionViewModels(isVisible: boolean,
    includeInternals: boolean, includeExternals: boolean, includeCollections: boolean) {
    this.relationViewModels.forEach(vm => {

      if (includeInternals && (vm as InternalViewModelInterface).initInternalViewModel) {
        vm.isVisible = isVisible;
      }

      if (includeExternals && (vm as ExternalViewModelInterface).initExternalViewModel) {
        vm.isVisible = isVisible;
      }

      if (includeCollections && (vm as CollectionViewModelInterface<any>).init) {
        vm.isVisible = isVisible;
      }
    });
  }

  onPropertyChanged(propertyName: string = null) {
    const args = new PropertyViewModelPropertyChangedEventArgs();
    args.propertyName = propertyName;
    this.propertyChanged.emit(args);
  }

  set domainModel(value: TModel) {
    this._domainModel = value;
  }
  get domainModel(): TModel {
    return this._domainModel;
  }

  protected get modifiedSubscriber() {
    return this._modifiedSubscriber;
  }

  override updateCurrentErrors() {
    const errors = this.getErrors();
    this.errors$.next(errors);
  }

  override getErrors(): Array<string> {
    const ret = this.messageContainerCollection.filter(x => x.isErrorMessage).map(x => x.message);

    if (this.propertyViewModels != null) {
      this.propertyViewModels.forEach((item, key) => {
        if (item.isEnabled && item.isVisible) {
          if (item.hasErrors) {
            ret.push(...item.getErrors());
          }
        }
      });
    }

    if (this.relationViewModels != null) {

      this.relationViewModels.forEach((item) => {
        // vengono controllati solo gli item abilitati e visibili
        if (item.isEnabled && item.isVisible) {
          if (item.hasErrors) {
            ret.push(...item.getErrors());
          }
        }
      });
    }

    return ret;

    // if (propertyName && propertyName === this.bindedValuePropertyName && this.messageContainerCollection.length > 0) {
    //     return this.messageContainerCollection.filter(x => x.isErrorMessage).map(x => x.message);
    // } else {
    //     return null;
    // }
  }

  override get hasErrors(): boolean {
    let ret = this.messageContainerCollection.length > 0;

    if (this.propertyViewModels != null) {
      this.propertyViewModels.forEach((item, key) => {
        if (item.isEnabled && item.isVisible) {
          if (item.hasErrors) {
            ret = true;
          }
        }
      });
    }

    if (ret === false && this.relationViewModels != null) {

      this.relationViewModels.forEach((item) => {
        // vengono controllati solo gli item abilitati e visibili
        if (item.isEnabled && item.isVisible) {
          if (item.hasErrors) {
            ret = true;
          }
        }
      });

    }

    return ret;
  }

  get currentState(): ViewModelStates {
    // Necessario perchè in alcuni casi potremmo aver bisogno di accedere
    // al getter anche quando il domain model non è caricato (buildPropertyLists())
    if (this.domainModel == null) { return null; }

    switch (this.domainModel.currentState) {
      case DomainModelState.New:
        return ViewModelStates.New;

      case DomainModelState.Unchanged:
        return ViewModelStates.Unchanged;

      case DomainModelState.Modified:
        return ViewModelStates.Modified;

      case DomainModelState.Removed:
        return ViewModelStates.Removed;

      default:
        throw new Error('Unknown State "' + this.domainModel.currentState + '" on ViewModel');
    }
  }

  constructor() {
    super();

    const mapMethods = [
      'get', 'set', 'has', 'delete', 'clear',
      'forEach', 'entries', 'keys', 'values',
      Symbol.iterator
    ];

    const self = this;

    this.relationViewModels = new Proxy(new Map<string, BaseViewModelInterface & ReservedNamePathInterface>(), {
      get(target, prop, receiver) {
        // Invalida la cache su set/delete/clear
        if (['set', 'delete', 'clear'].includes(prop as string)) {
          return (...args: any[]) => {
            const result = (target as any)[prop](...args);
            self._externalViewModelsDirty = true;
            self._internalViewModelsDirty = true;
            self._collectionViewModelsDirty = true;
            return result;
          };
        }
        // Restituisci i metodi nativi di Map bindati
        if (mapMethods.includes(prop as any)) {
          return (target as any)[prop].bind(target);
        }
        // Gestione della property size
        if (prop === 'size') {
          return target.size;
        }
        // Default
        return Reflect.get(target, prop, receiver);
      }
    });

    this.viewModelChangeDebouncer
      .pipe(
        takeUntil(this.destroySubscribers$),
        debounceTime(100)
      )
      .subscribe((propertyName) => this.viewModelChanged.emit(propertyName));

    this._genericsPropertiesType = GenericsPropertiesTypeInspector.getValue(this);
  }

  override removeError(item: MessageContainer) {
  }

  // Lancia l'onDestroy prima sui figli fino arrivare al padre
  override onDestroy() {

    this.propertyViewModels.forEach(p => {
      p.onDestroy();
    });
    this.relationViewModels.forEach(p => {
      p.onDestroy();
    });
    super.onDestroy();

    // this.domainModelName = null;
    // this.domainModelType = null;
    // this.domainModelFullName = null;
    // this.internalMetadataDescription = null;

    this.metadataDescription$?.complete();
    // this.metadataDescription$ = null;

    // this.internalMetadataShortDescription = null;

    this.metadataShortDescription$?.complete();
    // this.metadataShortDescription$ = null;

    // this.relationViewModels?.clear();
    // this.relationViewModels = null;

    // this.externalRetriever = null;
    // this.domainModelMetaData = null;

    // this.isMock = null;
    // this.reservedPath = null;
    // this.reservedName = null;

    // this.propertyViewModels?.clear();
    // this.propertyViewModels = null;

    // this.aggregateMetaData = null;
    this.propertyChanged?.complete();
    // this.propertyChanged = null;

    this.viewModelChanged?.complete();
    // this.viewModelChanged = null;

    this.viewModelChangeDebouncer?.complete();
    // this.viewModelChangeDebouncer = null;

    // this.eventDispatcher = null;

    // this.behindPropertyViewModels?.clear();
    // this.behindPropertyViewModels = null;

    // this._modifiedSubscriber = null;
    // this._layoutDefaultValueList?.clear();
    // this._layoutDefaultValueList = null;

    // this._externalViewModelsMap?.clear();
    // this._externalViewModelsMap = null;
    // this._internalViewModelsMap?.clear();
    // this._internalViewModelsMap = null;
    // this._externalViewModelsDirty = null;
    // this._internalViewModelsDirty = null;

    // this._collectionViewModelsMap?.clear();
    // this._collectionViewModelsMap = null;
    // this._collectionViewModelsDirty = null;

    this.internalExtension?.onDestroy();
    // this.internalExtension = null;

    // this._domainModel = null;
    // this._isRemoving = null;

    // this.#skipValidation = null;
    // this.#postInitFunctions = null;

  }

  override clearErrors() {
    if (this.hasErrors) {

      this.removeAllMessages();

      // cancello gli errori dai PropertyViewModel
      this.propertyViewModels.forEach((pvm, key) => {
        if (pvm.hasErrors) {
          pvm.clearErrors();
        }
      });

      if (this.relationViewModels != null) {
        // cancello gli errori dai viewModel child
        this.relationViewModels.forEach((vm) => {
          if (vm.hasErrors) {
            vm.clearErrors();
          }
        });
      }
    }
  }

  validatePropertyImplementation(propertyName: string) {
    const domainModelValidateResults = new Array<BaseErrorInterface>();
    this.domainModel.validateProperty(propertyName, domainModelValidateResults);

    for (const item of domainModelValidateResults) {

      let addToMessageContainerCollection = true;
      if (item.propertyName != null) {

        if (this.propertyViewModels.has(item.propertyName)) {
          this.propertyViewModels.get(item.propertyName).addError(SourceMessage.Validation, item);
          addToMessageContainerCollection = false;
        } else if (this.relationViewModels.has(item.propertyName)) {
          this.relationViewModels.get(item.propertyName).addError(SourceMessage.Validation, item);

          // Il metodo SetError è virtual e per il momento è implementato realmente solo sulle External e non sulle internal
          // pertanto nel caso di internal non ha fatto nulla e quindi dovrò poi aggiungere a mano alla collection
          // if ((<ExternalViewModelInterface>this.relationViewModels.get(item.propertyName)).findExternal != null) {
          addToMessageContainerCollection = false;
          // }
        } else {
          // appendo l'errore a livello di domain model
          // this.addError(SourceMessage.Validation, item);
          addToMessageContainerCollection = false;
        }

      }

      if (addToMessageContainerCollection) {
        // appendo l'errore a livello di domain model
        this.addError(SourceMessage.Validation, item);
        if (this['eventDispatcher']) {
          const ed = (this as any).eventDispatcher as ViewModelEventDispatcher;
          ed.onAddMessageInViewModel.next({ viewModel: this, messages: [item] });
        }
      }
    }
  }

  validateImplementation() {

    // ----------------------------------------------------------------------------------------------------
    // Property del ViewModel
    // ----------------------------------------------------------------------------------------------------
    // Questo torna già la lista completa degli errori di questo domain model (non va in ricorsione),
    // tranne quelli di eventuali di campi custom del viewModel
    const domainModelValidateResults = new Array<BaseErrorInterface>();
    this.domainModel.validateAllProperties(domainModelValidateResults, false);

    for (const item of domainModelValidateResults) {

      let addToMessageContainerCollection = true;
      if (item.propertyName != null) {

        if (this.propertyViewModels.has(item.propertyName)) {
          this.propertyViewModels.get(item.propertyName).addError(SourceMessage.Validation, item);
          addToMessageContainerCollection = false;
        } else if (this.relationViewModels.has(item.propertyName)) {
          this.relationViewModels.get(item.propertyName).addError(SourceMessage.Validation, item);

          // Il metodo SetError è virtual e per il momento è implementato realmente solo sulle External e non sulle internal
          // pertanto nel caso di internal non ha fatto nulla e quindi dovrò poi aggiungere a mano alla collection
          // if ((<ExternalViewModelInterface>this.relationViewModels.get(item.propertyName)).findExternal != null) {
          addToMessageContainerCollection = false;
          // }
        } else {
          // appendo l'errore a livello di domain model
          // this.addError(SourceMessage.Validation, item);
          addToMessageContainerCollection = false;
        }

      }

      if (addToMessageContainerCollection) {
        // appendo l'errore a livello di domain model
        this.addError(SourceMessage.Validation, item);
        if (this['eventDispatcher']) {
          const ed = (this as any).eventDispatcher as ViewModelEventDispatcher;
          ed.onAddMessageInViewModel.next({ viewModel: this, messages: [item] });
        }
      }
    }

    // prendo anche gli errori dei campi custom del ViewModel
    const customPVMList: Array<PropertyViewModelInterface> = new Array<PropertyViewModelInterface>();

    // lancia la validate su tutte le pvm
    this.propertyViewModels.forEach(p => {
      if (p.isCustom) {
        customPVMList.push(p);
      } else {
        p.validate();
      }
    });

    customPVMList.forEach(customPVM => {
      // TODO CustomPVM validation!
      customPVM.validate();
      // var res = customPVM.validate();
      // if (res.Count > 0)
      // {
      //     results.AddRange(res);
      //     customPVM.SetErrors(SourceMessage.Validation, res.Select(item => item.ErrorMessage).ToList());
      // }
    });
  }

  override validate() {
    if (this.skipValidation || this.isMock) {
      return;
    }

    // cancello gli errori su tutte le property
    this.clearErrors();

    // NOTA: al momento la nostra gestione non prevede che una eventuale internal "obbligatoria" sia presente senza
    // domainModel, ovvero: se è obbligatoria allora domainModel è stato sicuramente creato, se invece il domainModel
    // non c'è deve per forza essere non-obbligatorio e quindi non faccio nemmeno le validazioni
    if (this.domainModel != null) {

      // ----------------------------------------------------------------------------------------------------
      // ChildViewModel
      // ----------------------------------------------------------------------------------------------------
      // Parto con la validazione dei ChildViewModel (ricorsiva).
      // Questo approccio è più prudente, dato che i figli validano se stessi ma poi i padri potrebbero aggiungere sui figli
      // altri errori che dipendono da elementi più in alto nella gerarchia e che pertanto i figli non potrebbero conoscere;
      // i figli, come ogni domain model, iniziano la loro validazione resettando i propri errori, pertanto è giusto che gli errori
      // aggiunti dai padri vengano aggiunti "dopo"
      if (this.relationViewModels != null) {
        this.relationViewModels.forEach(child => {
          child.validate();
        });
      }

      this.validateImplementation();
    }
  }

  override validateProperty(propertyName: string) {
    if (this.skipValidation || this.isMock) {
      return;
    }

    // cancello gli errori solo della property
    this.removePropertyErrors(propertyName);

    if (this.domainModel != null) {
      this.validatePropertyImplementation(propertyName);
    }
  }

  removePropertyErrors(propertyName: string) {
    let property = this.getProperty(propertyName) as BaseViewModelInterface;

    if (!property) {
      property = this.relationViewModels.get(propertyName);
    }

    if (property?.messageContainerCollection) {
      for (const message of property.messageContainerCollection) {
        if (message.propertyName === propertyName) {
          property.removeError(message);
        }
      }
    }
  }

  signObjectToRemove() {
    this._isRemoving = true;
  }

  getDomainModel(): TModel {
    return this.domainModel;
  }

  getProperty(propertyName: string): PropertyViewModelInterface {
    if (this.propertyViewModels.has(propertyName)) {
      return this.propertyViewModels.get(propertyName);
    } else {
      return null;
    }
  }

  getProperties(): PropertyViewModelInterface[] {
    const arr = new Array<PropertyViewModelInterface>();
    this.propertyViewModels.forEach((v) => {
      arr.push(v);
    });
    return arr;
  }

  switchIsEnabledForPropertyViewModels(isEnabled: boolean) {
    this.propertyViewModels.forEach((value, key) => {
      value.isEnabled = isEnabled;
    });
  }

  switchIsEnabledForInternalExternalCollectionViewModels(isEnabled: boolean,
    includeInternals: boolean, includeExternals: boolean, includeCollections: boolean) {
    this.relationViewModels.forEach(vm => {

      if (includeInternals && (vm as InternalViewModelInterface).initInternalViewModel) {
        vm.isEnabled = isEnabled;
      }

      if (includeExternals && (vm as ExternalViewModelInterface).initExternalViewModel) {
        vm.isEnabled = isEnabled;
      }

      if (includeCollections && (vm as CollectionViewModelInterface<any>).init) {
        vm.isEnabled = isEnabled;
      }
    });
  }

  getGenericPropertyType(propertyName: string) {
    if (!this._genericsPropertiesType || this._genericsPropertiesType[propertyName] == null) {
      throw new Error(
        `MetaData ${GenericsPropertiesTypeInspector.META_DATA_KEY} not defined. You must use ${GenericsPropertiesTypeInspector.DECORATOR_NAME} in ${this.constructor.name} and define ${propertyName}.`
      );
    }
    return this._genericsPropertiesType[propertyName];
  }

  async preInit(): Promise<void> { }

  async postInit(): Promise<void> {
    this.disableIdentities();

    if (this.#postInitFunctions.length > 0) {
      await Promise.all(
        this.#postInitFunctions.map(fn => fn())
      );

      this.#postInitFunctions = [];
    }
  }

  appendToPostInit(fn: () => Promise<any>) {
    this.#postInitFunctions.push(fn);
  }

  static getGenericPropertyType(propertyName: string) {
    const decoratorData = GenericsPropertiesTypeInspector.getValue(this);
    if (!decoratorData) {
      throw new Error(
        `MetaData ${GenericsPropertiesTypeInspector.META_DATA_KEY} not defined. You must use ${GenericsPropertiesTypeInspector.DECORATOR_NAME} in ${this.constructor.name} and define ${propertyName}.`
      );
    } else if (decoratorData[propertyName] == null) {
      throw new Error(
        `Decorator ${GenericsPropertiesTypeInspector.DECORATOR_NAME} found in ${this.constructor.name} but you should and define ${propertyName}.`
      );
    }
    return decoratorData[propertyName];
  }

  /// <summary>
  /// Cancella tutti gli errori di tutti i propertyViewModel, senza andare in ricorsione
  /// sui ChildViewModel. Solleva poi l'evento di OnPropertyChanged("HasErrors")
  /// </summary>
  protected resetErrorsForFullValidateClient() {
    if (this.hasErrors) {
      // errori sulle property
      this.propertyViewModels.forEach(pvm => {
        if (pvm.hasErrors) {
          pvm.clearErrors();
        }
      });

      // Cancello gli errori rimanenti, ovvero errori di VM che non sono di specifiche
      // property (vengono di solito da una validateDomainModel o da validazione di Api client)
      const list = this.messageContainerCollection;
      list.forEach(item => {
        // se questo errore è su una ChildViewModel potrebbe essere un classico errore di tipo ValidateDecode che
        // verrebbe resettato quando la validate va in ricorsione, ma potrebbe anche essere un errore in generale
        // di tipo Validate che non viene resettato dalla Validate della external, quindi devo resettarlo qui
        // NOTA: per adesso la SetError e la RemoveError sono metodi virtual realmente implementate solo sulle ExternalViewModel e non sulle internal;
        // sulle internal quindi non fanno nulla e quindi per essere sicuro che sia rimosso dalla MessageContainerCollection non posso
        // farla eseguire nei casi di internal, ma solo nei casi di external. Nei casi di internal rimuovo "a mano" dalla MessageContainerCollection
        // if (this.relationViewModels != null && item.propertyName && this.relationViewModels.has(item.propertyName)
        //     && item.sourceMessage !== SourceMessage.ValidationDecode && (<ExternalViewModelInterface>this.relationViewModels.get(item.propertyName)).findExternal != null) {
        //     this.relationViewModels.get(item.propertyName).removeError(item);
        // } else {
        this.messageContainerCollection.splice(this.messageContainerCollection.indexOf(item), 1);
        // }
      });
    }

  }

  protected disableIdentities() {
    // Disabilito le identity in base ai decoratori o allo stato del viewmodel
    this.domainModelMetaData?.identityNames.filter((identityPropertyName: string) =>
      !MetaDataUtils.checkIfPropertyIsACodeForExternal(this.domainModelMetaData, identityPropertyName)
    ).forEach((identityName: string) => {
      const identity = this.getProperty(MetaDataUtils.toCamelCase(identityName));

      // Può succedere che la identity non viene dichiarata nel viewmodel quindi identity sarà null.
      // In questo caso non devo fare niente
      if (identity && identity.propertyMetaData.getType() === 'Numeric') {
        const propertyMetaData: NumericMetaData = identity.propertyMetaData as NumericMetaData;
        identity.isEnabled = !propertyMetaData.isAutoComputed;
      }
      if (identity && (this.currentState === ViewModelStates.Modified || this.currentState === ViewModelStates.Unchanged)) {
        identity.isEnabled = false;
      }
    });
  }

  // Inizializzo il viewmodel dai metadati e i suoi property view model
  protected initCustomViewModel(
    domainModel: TModel,
    aggregateMetaData: AggregateMetaDataInterface,
    domainModelName: string,
    domainModelType: ClassConstructor<TModel>,
    externalRetriever?: ExternalRetrieverInterface,
    eventDispatcher?: ViewModelEventDispatcher,
  ): void {

    if (aggregateMetaData != null) {
      this.aggregateMetaData = aggregateMetaData;
      this.domainModel = domainModel;
      this.externalRetriever = externalRetriever;
      this.eventDispatcher = eventDispatcher;

      if (domainModelName != null) {
        this.domainModelName = domainModelName;
      } else if (this.domainModel != null) {
        this.domainModelName = this.domainModel.modelTypeName;
      }

      if (domainModelType != null) {
        this.domainModelType = domainModelType;
      }

      const domainModelMetaData = aggregateMetaData.domainModels.find(
        (element: DomainModelMetaDataInterface) => MetaDataUtils.toCamelCase(element?.name ?? '') === MetaDataUtils.toCamelCase(this.domainModelName)
      );
      if (!domainModelMetaData) {
        LogService.warn(
          `DomainModelName '${MetaDataUtils.toCamelCase(this.domainModelName)}' non trovato. Verfica il nome del decoratore @ModelTypeNameDecorator`);

      }
      if (domainModelMetaData != null) {
        this.domainModelMetaData = domainModelMetaData;
        this.domainModelFullName = domainModelMetaData.fullName;
        this.buildCustomPropertyLists();
        this.metadataShortDescription = this.domainModelMetaData.descriptions.displayName;
        this.metadataDescription = this.domainModelMetaData.descriptions.description;
      }

    }
  }

  // Inizializzo il viewmodel dai metadati e i suoi property view model
  protected async initViewModel(
    domainModel: TModel,
    aggregateMetaData: AggregateMetaDataInterface,
    orchestratorViewModel: CoreOrchestratorViewModelInterface,
    domainModelName: string,
    domainModelType: ClassConstructor<TModel>
  ): Promise<void> {

    if (aggregateMetaData != null) {
      this.aggregateMetaData = aggregateMetaData;
      this._modifiedSubscriber = orchestratorViewModel;
      this.externalRetriever = orchestratorViewModel;
      if (orchestratorViewModel) {
        this.eventDispatcher = orchestratorViewModel.eventDispatcher;
        this._layoutDefaultValueList = orchestratorViewModel.layoutDefaultValueList;
      }
      this.domainModel = domainModel;

      if (domainModelName != null) {
        this.domainModelName = domainModelName;
      } else if (this.domainModel != null) {
        this.domainModelName = this.domainModel.modelTypeName;
      }

      if (domainModelType != null) {
        this.domainModelType = domainModelType;
      }

      const domainModelMetaData = aggregateMetaData.domainModels.find(
        (element: DomainModelMetaDataInterface) => MetaDataUtils.toCamelCase(element?.name ?? '') === MetaDataUtils.toCamelCase(this.domainModelName)
      );
      if (!domainModelMetaData) {
        LogService.warn(
          `DomainModelName '${MetaDataUtils.toCamelCase(this.domainModelName)}' non trovato. Verfica il nome del decoratore @ModelTypeNameDecorator`);

      }
      if (domainModelMetaData != null) {
        this.domainModelMetaData = domainModelMetaData;
        this.domainModelFullName = domainModelMetaData.fullName;
        await this.buildPropertyLists();
        this.metadataShortDescription = orchestratorViewModel?.useMessageResourceKey ? MessageResourceManager.Current.getMessageIfExists(this.domainModelMetaData.descriptions.displayNameKey) : this.domainModelMetaData.descriptions.displayName;
        this.metadataDescription = orchestratorViewModel?.useMessageResourceKey ? MessageResourceManager.Current.getMessageIfExists(this.domainModelMetaData.descriptions.descriptionKey) : this.domainModelMetaData.descriptions.description;
      }

    }
  }

  // Popolo la lista di tutti i property view models
  // Attenzione al momento i pvm custom non sono gestiti e popolati in propertyViewModels
  protected async buildCustomPropertyLists(): Promise<void> {
    this.propertyViewModels = new Map<string, PropertyViewModelInterface>();
    const allProperties = this.domainModelMetaData.allPropertyNames
      .map((val) => MetaDataUtils.toCamelCase(val));
    for (const key of allProperties) {
      const propertyValue = this[key];
      if (propertyValue != null && propertyValue instanceof PropertyViewModel) {
        this.propertyViewModels.set(key, propertyValue);

        // Imposto il parent
        propertyValue.parent = this;

        // Imposto la security
        propertyValue.securityAccess = this.domainModelMetaData.getPropertyMetaData(key)?.userMetaData?.securityAccess;

        // #1625 aggiunto per gestire il fallback sui campi numerici required non nullable
        if (propertyValue.isRequired && propertyValue instanceof NumericPropertyViewModel && propertyValue.value == null) {
          const oldValue = propertyValue.canNotifyModified;
          propertyValue.canNotifyModified = false;
          await propertyValue.resetValue(true);
          propertyValue.canNotifyModified = oldValue;
        }

        // #3312 aggiunto per gestire il fallback sui campi enum non nullable, imposta il valore della prima opzione disponibile
        if (propertyValue instanceof EnumPropertyViewModel && propertyValue.value == null) {
          const oldValue = propertyValue.canNotifyModified;
          propertyValue.canNotifyModified = false;
          await propertyValue.resetValue(true);
          propertyValue.canNotifyModified = oldValue;
        }

        // aggiunto per gestire il fallback sui campi date non nullable, imposta la data di oggi
        if (propertyValue instanceof DateTimePropertyViewModel && propertyValue.value == null) {
          const oldValue = propertyValue.canNotifyModified;
          propertyValue.canNotifyModified = false;
          await propertyValue.resetValue(true);
          propertyValue.canNotifyModified = oldValue;
        }

        if (
          (this.domainModel?.isMock === false) && this.currentState === ViewModelStates.New ||
          (this.domainModel?.forceDefaultValue === true) && this.currentState === ViewModelStates.New
        ) {
          await propertyValue.setCurrentValueWithDefaultValueFromLayoutMetaData();
        }

        merge(
          propertyValue.propertyViewModelChanged,
          propertyValue.propertyChanged.pipe(filter((args) => args.propertyName === propertyValue.bindedValuePropertyName))
        ).pipe(takeUntil(this.destroySubscribers$)).subscribe(e => {
          this.viewModelChangeDebouncer.next(propertyValue.propertyName);
        });
      }
      if (propertyValue?.formattedValue$) {
        propertyValue.formattedValue$.next(propertyValue.formattedValue);
      }
      if (propertyValue?.value$) {
        propertyValue.value$.next(propertyValue.value);
      }

    }
  }

  async buildExtensionPropertyViewModels(): Promise<void> {
    // Costruisco i property view model custom definiti nell'extension
    await this.extension?.buildPropertyViewModels();
  }

  // Popolo la lista di tutti i property view models
  // Attenzione al momento i pvm custom non sono gestiti e popolati in propertyViewModels
  protected async buildPropertyLists(): Promise<void> {
    await this.buildExtensionPropertyViewModels();

    this.propertyViewModels = new Map<string, PropertyViewModelInterface>();
    const allProperties = this.domainModelMetaData.allPropertyNames
      .map((val) => MetaDataUtils.toCamelCase(val));
    for (const key of allProperties) {

      let propertyValue = this[key];

      if (this.extension?.propertyViewModels.has(key)) {
        propertyValue = this.extension?.propertyViewModels.get(key);
      }

      if (propertyValue != null && propertyValue instanceof PropertyViewModel) {
        this.propertyViewModels.set(key, propertyValue);

        // Imposto il parent
        propertyValue.parent = this;

        // Imposto la security
        propertyValue.securityAccess = this.domainModelMetaData.getPropertyMetaData(key)?.userMetaData?.securityAccess;

        // #1625 aggiunto per gestire il fallback sui campi numerici required non nullable
        if (propertyValue.isRequired && propertyValue instanceof NumericPropertyViewModel && propertyValue.value == null) {
          const oldValue = propertyValue.canNotifyModified;
          propertyValue.canNotifyModified = false;
          await propertyValue.resetValue(true);
          propertyValue.canNotifyModified = oldValue;
        }

        // #3312 aggiunto per gestire il fallback sui campi enum non nullable, imposta il valore della prima opzione disponibile
        if (propertyValue instanceof EnumPropertyViewModel && propertyValue.value == null) {
          const oldValue = propertyValue.canNotifyModified;
          propertyValue.canNotifyModified = false;
          await propertyValue.resetValue(true);
          propertyValue.canNotifyModified = oldValue;
        }

        // aggiunto per gestire il fallback sui campi date non nullable, imposta la data di oggi
        if (propertyValue instanceof DateTimePropertyViewModel && propertyValue.value == null) {
          const oldValue = propertyValue.canNotifyModified;
          propertyValue.canNotifyModified = false;
          await propertyValue.resetValue(true);
          propertyValue.canNotifyModified = oldValue;
        }

        if (
          (this.domainModel?.isMock === false) && this.currentState === ViewModelStates.New ||
          (this.domainModel?.forceDefaultValue === true) && this.currentState === ViewModelStates.New
        ) {
          await propertyValue.setCurrentValueWithDefaultValueFromLayoutMetaData();
        }

        merge(
          propertyValue.propertyViewModelChanged,
          propertyValue.propertyChanged.pipe(filter((args) => args.propertyName === propertyValue.bindedValuePropertyName))
        ).pipe(takeUntil(this.destroySubscribers$)).subscribe(e => {
          this.viewModelChangeDebouncer.next(propertyValue.propertyName);
        });
      }
      if (propertyValue?.formattedValue$) {
        propertyValue.formattedValue$.next(propertyValue.formattedValue);
      }
      if (propertyValue?.value$) {
        propertyValue.value$.next(propertyValue.value);
      }

    }
  }

  //#region getStringPropertyViewModel
  getStringPropertyViewModel<TStringPropertyViewModel extends StringPropertyViewModelInterface = StringPropertyViewModel>(
    params: GetGenericPropertyViewModelInterface<string, TStringPropertyViewModel>
  ): TStringPropertyViewModel;
  /**
   * @deprecated utilizza getStringPropertyViewModel con in input l'object params e non l'action
   */
  getStringPropertyViewModel<TStringPropertyViewModel extends StringPropertyViewModelInterface = StringPropertyViewModel>(
    action: (value: TStringPropertyViewModel) => void,
    value: TStringPropertyViewModel,
    propertyName: string,
    customGetter?: () => string,
    customSetter?: (value: string) => Promise<void>,
    propertyViewModelType?: ClassConstructor<TStringPropertyViewModel>,
    additionalCustomValidation?: () => BaseErrorInterface[],
    additionalObserverForValuePropertyChange?: Observable<any>
  ): TStringPropertyViewModel;
  /**
   * Restituisce un PropertyViewModel String
   */
  getStringPropertyViewModel<TStringPropertyViewModel extends StringPropertyViewModelInterface = StringPropertyViewModel>(
    action: GetGenericPropertyViewModelInterface<string, TStringPropertyViewModel> | ((value: TStringPropertyViewModel) => void),
    value: TStringPropertyViewModel | undefined = undefined,
    propertyName: string = '',
    customGetter: undefined | (() => string) = undefined,
    customSetter: undefined | ((value: string) => Promise<void>) = undefined,
    propertyViewModelType: undefined | ClassConstructor<TStringPropertyViewModel> = undefined,
    additionalCustomValidation: undefined | (() => BaseErrorInterface[]) = undefined,
    additionalObserverForValuePropertyChange: undefined | Observable<any> = undefined,
  ): TStringPropertyViewModel {
    return this.getGenericPropertyViewModel<string, TStringPropertyViewModel, StringMetaDataInterface>({
      action,
      value,
      propertyName,
      customGetter,
      customSetter,
      propertyViewModelType: (propertyViewModelType ?? StringPropertyViewModel) as ClassConstructor<TStringPropertyViewModel>,
      additionalCustomValidation,
      additionalObserverForValuePropertyChange,
      metaDataList: this.domainModelMetaData.strings
    });
  }
  //#endregion getStringPropertyViewModel

  //#region getGuidPropertyViewModel
  getGuidPropertyViewModel<TGuidPropertyViewModel extends GuidPropertyViewModel = GuidPropertyViewModel>(
    params: GetGenericPropertyViewModelInterface<string, TGuidPropertyViewModel>
  ): TGuidPropertyViewModel;
  /**
   * @deprecated utilizza getGuidPropertyViewModel con in input l'object params e non l'action
   */
  getGuidPropertyViewModel<TGuidPropertyViewModel extends GuidPropertyViewModel = GuidPropertyViewModel>(
    action: (value: TGuidPropertyViewModel) => void,
    value: TGuidPropertyViewModel,
    propertyName: string,
    customGetter?: () => string,
    customSetter?: (value: string) => Promise<void>,
    propertyViewModelType?: ClassConstructor<TGuidPropertyViewModel>,
    additionalCustomValidation?: () => BaseErrorInterface[],
    additionalObserverForValuePropertyChange?: Observable<any>
  ): TGuidPropertyViewModel;
  /**
   * Restituisce un PropertyViewModel Guid
   */
  getGuidPropertyViewModel<TGuidPropertyViewModel extends GuidPropertyViewModel = GuidPropertyViewModel>(
    action: GetGenericPropertyViewModelInterface<string, TGuidPropertyViewModel> | ((value: TGuidPropertyViewModel) => void),
    value: TGuidPropertyViewModel | undefined = undefined,
    propertyName: string = '',
    customGetter: undefined | (() => string) = undefined,
    customSetter: undefined | ((value: string) => Promise<void>) = undefined,
    propertyViewModelType: undefined | ClassConstructor<TGuidPropertyViewModel> = undefined,
    additionalCustomValidation: undefined | (() => BaseErrorInterface[]) = undefined,
    additionalObserverForValuePropertyChange: undefined | Observable<any> = undefined,
  ): TGuidPropertyViewModel {
    return this.getGenericPropertyViewModel<string, TGuidPropertyViewModel, GuidMetaDataInterface>({
      action,
      value,
      propertyName,
      customGetter,
      customSetter,
      propertyViewModelType: (propertyViewModelType ?? GuidPropertyViewModel) as ClassConstructor<TGuidPropertyViewModel>,
      additionalCustomValidation,
      additionalObserverForValuePropertyChange,
      metaDataList: this.domainModelMetaData.guids
    });
  }
  //#endregion getGuidPropertyViewModel

  //#region getVersionPropertyViewModel
  getVersionPropertyViewModel<TVersionPropertyViewModel extends VersionPropertyViewModelInterface = VersionPropertyViewModelInterface>(
    params: GetGenericPropertyViewModelInterface<string, TVersionPropertyViewModel>
  ): TVersionPropertyViewModel;
  /**
   * @deprecated utilizza getVersionPropertyViewModel con in input l'object params e non l'action
   */
  getVersionPropertyViewModel<TVersionPropertyViewModel extends VersionPropertyViewModelInterface = VersionPropertyViewModelInterface>(
    action: (value: TVersionPropertyViewModel) => void,
    value: TVersionPropertyViewModel,
    propertyName: string,
    customGetter?: () => string,
    customSetter?: (value: string) => Promise<void>,
    propertyViewModelType?: ClassConstructor<TVersionPropertyViewModel>,
    additionalCustomValidation?: () => BaseErrorInterface[],
    additionalObserverForValuePropertyChange?: Observable<any>
  ): TVersionPropertyViewModel;
  /**
   * Restituisce un PropertyViewModel Version
   */
  getVersionPropertyViewModel<TVersionPropertyViewModel extends VersionPropertyViewModelInterface = VersionPropertyViewModelInterface>(
    action: GetGenericPropertyViewModelInterface<string, TVersionPropertyViewModel> | ((value: TVersionPropertyViewModel) => void),
    value: TVersionPropertyViewModel | undefined = undefined,
    propertyName: string = '',
    customGetter: undefined | (() => string) = undefined,
    customSetter: undefined | ((value: string) => Promise<void>) = undefined,
    propertyViewModelType: undefined | ClassConstructor<TVersionPropertyViewModel> = undefined,
    additionalCustomValidation: undefined | (() => BaseErrorInterface[]) = undefined,
    additionalObserverForValuePropertyChange: undefined | Observable<any> = undefined,
  ): TVersionPropertyViewModel {
    return this.getGenericPropertyViewModel<string, TVersionPropertyViewModel, ModelVersionMetaDataInterface>({
      action,
      value,
      propertyName,
      customGetter,
      customSetter,
      propertyViewModelType: (propertyViewModelType ?? VersionPropertyViewModel) as ClassConstructor<TVersionPropertyViewModel>,
      additionalCustomValidation,
      additionalObserverForValuePropertyChange,
      metaDataList: [this.domainModelMetaData.version]
    });
  }
  //#endregion getVersionPropertyViewModel

  //#region getTimeSpanPropertyViewModel
  getTimeSpanPropertyViewModel<TTimeSpanPropertyViewModel extends TimeSpanPropertyViewModel = TimeSpanPropertyViewModel>(
    params: GetGenericPropertyViewModelInterface<TimeSpan, TTimeSpanPropertyViewModel>
  ): TTimeSpanPropertyViewModel;
  /**
   * @deprecated utilizza getTimeSpanPropertyViewModel con in input l'object params e non l'action
   */
  getTimeSpanPropertyViewModel<TTimeSpanPropertyViewModel extends TimeSpanPropertyViewModel = TimeSpanPropertyViewModel>(
    action: (value: TTimeSpanPropertyViewModel) => void,
    value: TTimeSpanPropertyViewModel,
    propertyName: string,
    customGetter?: () => TimeSpan,
    customSetter?: (value: TimeSpan) => Promise<void>,
    propertyViewModelType?: ClassConstructor<TTimeSpanPropertyViewModel>,
    additionalCustomValidation?: () => BaseErrorInterface[],
    additionalObserverForValuePropertyChange?: Observable<any>
  ): TTimeSpanPropertyViewModel;
  /**
   * Restituisce un PropertyViewModel TimeSpan Non Nullable
   */
  getTimeSpanPropertyViewModel<TTimeSpanPropertyViewModel extends TimeSpanPropertyViewModel = TimeSpanPropertyViewModel>(
    action: GetGenericPropertyViewModelInterface<TimeSpan, TTimeSpanPropertyViewModel> | ((value: TTimeSpanPropertyViewModel) => void),
    value: TTimeSpanPropertyViewModel | undefined = undefined,
    propertyName: string = '',
    customGetter: undefined | (() => TimeSpan) = undefined,
    customSetter: undefined | ((value: TimeSpan) => Promise<void>) = undefined,
    propertyViewModelType: undefined | ClassConstructor<TTimeSpanPropertyViewModel> = undefined,
    additionalCustomValidation: undefined | (() => BaseErrorInterface[]) = undefined,
    additionalObserverForValuePropertyChange: undefined | Observable<any> = undefined,
  ): TTimeSpanPropertyViewModel {
    return this.getGenericPropertyViewModel<TimeSpan, TTimeSpanPropertyViewModel, TimeSpanMetaDataInterface>({
      action,
      value,
      propertyName,
      customGetter,
      customSetter,
      propertyViewModelType: (propertyViewModelType ?? TimeSpanPropertyViewModel) as ClassConstructor<TTimeSpanPropertyViewModel>,
      additionalCustomValidation,
      additionalObserverForValuePropertyChange,
      metaDataList: this.domainModelMetaData.timeSpans
    });
  }
  //#endregion getTimeSpanPropertyViewModel

  //#region getNTimeSpanPropertyViewModel
  getNTimeSpanPropertyViewModel<TNTimeSpanPropertyViewModel extends NTimeSpanPropertyViewModel = NTimeSpanPropertyViewModel>(
    params: GetGenericPropertyViewModelInterface<TimeSpan, TNTimeSpanPropertyViewModel>
  ): TNTimeSpanPropertyViewModel;
  /**
   * @deprecated utilizza getNTimeSpanPropertyViewModel con in input l'object params e non l'action
   */
  getNTimeSpanPropertyViewModel<TNTimeSpanPropertyViewModel extends NTimeSpanPropertyViewModel = NTimeSpanPropertyViewModel>(
    action: (value: TNTimeSpanPropertyViewModel) => void,
    value: TNTimeSpanPropertyViewModel,
    propertyName: string,
    customGetter?: () => TimeSpan,
    customSetter?: (value: TimeSpan) => Promise<void>,
    propertyViewModelType?: ClassConstructor<TNTimeSpanPropertyViewModel>,
    additionalCustomValidation?: () => BaseErrorInterface[],
    additionalObserverForValuePropertyChange?: Observable<any>
  ): TNTimeSpanPropertyViewModel;
  /**
   * Restituisce un PropertyViewModel TimeSpan Nullable
   */
  getNTimeSpanPropertyViewModel<TNTimeSpanPropertyViewModel extends NTimeSpanPropertyViewModel = NTimeSpanPropertyViewModel>(
    action: GetGenericPropertyViewModelInterface<TimeSpan, TNTimeSpanPropertyViewModel> | ((value: TNTimeSpanPropertyViewModel) => void),
    value: TNTimeSpanPropertyViewModel | undefined = undefined,
    propertyName: string = '',
    customGetter: undefined | (() => TimeSpan) = undefined,
    customSetter: undefined | ((value: TimeSpan) => Promise<void>) = undefined,
    propertyViewModelType: undefined | ClassConstructor<TNTimeSpanPropertyViewModel> = undefined,
    additionalCustomValidation: undefined | (() => BaseErrorInterface[]) = undefined,
    additionalObserverForValuePropertyChange: undefined | Observable<any> = undefined,
  ): TNTimeSpanPropertyViewModel {
    return this.getGenericPropertyViewModel<TimeSpan, TNTimeSpanPropertyViewModel, TimeSpanMetaDataInterface>({
      action,
      value,
      propertyName,
      customGetter,
      customSetter,
      propertyViewModelType: (propertyViewModelType ?? NTimeSpanPropertyViewModel) as ClassConstructor<TNTimeSpanPropertyViewModel>,
      additionalCustomValidation,
      additionalObserverForValuePropertyChange,
      metaDataList: this.domainModelMetaData.timeSpans
    });
  }
  //#endregion getNTimeSpanPropertyViewModel

  //#region getDateTimeOffsetPropertyViewModel
  getDateTimeOffsetPropertyViewModel<TDateTimeOffsetPropertyViewModel extends DateTimeOffsetPropertyViewModel = DateTimeOffsetPropertyViewModel>(
    params: GetGenericPropertyViewModelInterface<DateTimeOffset, TDateTimeOffsetPropertyViewModel>
  ): TDateTimeOffsetPropertyViewModel;
  /**
   * @deprecated utilizza getDateTimeOffsetPropertyViewModel con in input l'object params e non l'action
   */
  getDateTimeOffsetPropertyViewModel<TDateTimeOffsetPropertyViewModel extends DateTimeOffsetPropertyViewModel = DateTimeOffsetPropertyViewModel>(
    action: (value: TDateTimeOffsetPropertyViewModel) => void,
    value: TDateTimeOffsetPropertyViewModel,
    propertyName: string,
    customGetter?: () => DateTimeOffset,
    customSetter?: (value: DateTimeOffset) => Promise<void>,
    propertyViewModelType?: ClassConstructor<TDateTimeOffsetPropertyViewModel>,
    additionalCustomValidation?: () => BaseErrorInterface[],
    additionalObserverForValuePropertyChange?: Observable<any>
  ): TDateTimeOffsetPropertyViewModel;
  /**
   * Restituisce un PropertyViewModel DateTimeOffset Non Nullable
   */
  getDateTimeOffsetPropertyViewModel<TDateTimeOffsetPropertyViewModel extends DateTimeOffsetPropertyViewModel = DateTimeOffsetPropertyViewModel>(
    action: GetGenericPropertyViewModelInterface<DateTimeOffset, TDateTimeOffsetPropertyViewModel> | ((value: TDateTimeOffsetPropertyViewModel) => void),
    value: TDateTimeOffsetPropertyViewModel | undefined = undefined,
    propertyName: string = '',
    customGetter: undefined | (() => DateTimeOffset) = undefined,
    customSetter: undefined | ((value: DateTimeOffset) => Promise<void>) = undefined,
    propertyViewModelType: undefined | ClassConstructor<TDateTimeOffsetPropertyViewModel> = undefined,
    additionalCustomValidation: undefined | (() => BaseErrorInterface[]) = undefined,
    additionalObserverForValuePropertyChange: undefined | Observable<any> = undefined,
  ): TDateTimeOffsetPropertyViewModel {
    return this.getGenericPropertyViewModel<DateTimeOffset, TDateTimeOffsetPropertyViewModel, DateTimeMetaDataInterface>({
      action,
      value,
      propertyName,
      customGetter,
      customSetter,
      propertyViewModelType: (propertyViewModelType ?? DateTimeOffsetPropertyViewModel) as ClassConstructor<TDateTimeOffsetPropertyViewModel>,
      additionalCustomValidation,
      additionalObserverForValuePropertyChange,
      metaDataList: this.domainModelMetaData.dateTimes
    });
  }
  //#endregion getDateTimeOffsetPropertyViewModel

  //#region getNDateTimeOffsetPropertyViewModel
  getNDateTimeOffsetPropertyViewModel<TNDateTimeOffsetPropertyViewModel extends NDateTimeOffsetPropertyViewModel = NDateTimeOffsetPropertyViewModel>(
    params: GetGenericPropertyViewModelInterface<DateTimeOffset, TNDateTimeOffsetPropertyViewModel>
  ): TNDateTimeOffsetPropertyViewModel;
  /**
   * @deprecated utilizza getNDateTimeOffsetPropertyViewModel con in input l'object params e non l'action
   */
  getNDateTimeOffsetPropertyViewModel<TNDateTimeOffsetPropertyViewModel extends NDateTimeOffsetPropertyViewModel = NDateTimeOffsetPropertyViewModel>(
    action: (value: TNDateTimeOffsetPropertyViewModel) => void,
    value: TNDateTimeOffsetPropertyViewModel,
    propertyName: string,
    customGetter?: () => DateTimeOffset,
    customSetter?: (value: DateTimeOffset) => Promise<void>,
    propertyViewModelType?: ClassConstructor<TNDateTimeOffsetPropertyViewModel>,
    additionalCustomValidation?: () => BaseErrorInterface[],
    additionalObserverForValuePropertyChange?: Observable<any>
  ): TNDateTimeOffsetPropertyViewModel;
  /**
   * Restituisce un PropertyViewModel DateTimeOffset Nullable
   */
  getNDateTimeOffsetPropertyViewModel<TNDateTimeOffsetPropertyViewModel extends NDateTimeOffsetPropertyViewModel = NDateTimeOffsetPropertyViewModel>(
    action: GetGenericPropertyViewModelInterface<DateTimeOffset, TNDateTimeOffsetPropertyViewModel> | ((value: TNDateTimeOffsetPropertyViewModel) => void),
    value: TNDateTimeOffsetPropertyViewModel | undefined = undefined,
    propertyName: string = '',
    customGetter: undefined | (() => DateTimeOffset) = undefined,
    customSetter: undefined | ((value: DateTimeOffset) => Promise<void>) = undefined,
    propertyViewModelType: undefined | ClassConstructor<TNDateTimeOffsetPropertyViewModel> = undefined,
    additionalCustomValidation: undefined | (() => BaseErrorInterface[]) = undefined,
    additionalObserverForValuePropertyChange: undefined | Observable<any> = undefined,
  ): TNDateTimeOffsetPropertyViewModel {
    return this.getGenericPropertyViewModel<DateTimeOffset, TNDateTimeOffsetPropertyViewModel, DateTimeMetaDataInterface>({
      action,
      value,
      propertyName,
      customGetter,
      customSetter,
      propertyViewModelType: (propertyViewModelType ?? NDateTimeOffsetPropertyViewModel) as ClassConstructor<TNDateTimeOffsetPropertyViewModel>,
      additionalCustomValidation,
      additionalObserverForValuePropertyChange,
      metaDataList: this.domainModelMetaData.dateTimes
    });
  }
  //#endregion getNDateTimeOffsetPropertyViewModel

  //#region getDateTimePropertyViewModel
  getDateTimePropertyViewModel<TDateTimePropertyViewModel extends DateTimePropertyViewModel = DateTimePropertyViewModel>(
    params: GetGenericPropertyViewModelInterface<Date, TDateTimePropertyViewModel>
  ): TDateTimePropertyViewModel;
  /**
   * @deprecated utilizza getDateTimePropertyViewModel con in input l'object params e non l'action
   */
  getDateTimePropertyViewModel<TDateTimePropertyViewModel extends DateTimePropertyViewModel = DateTimePropertyViewModel>(
    action: (value: TDateTimePropertyViewModel) => void,
    value: TDateTimePropertyViewModel,
    propertyName: string,
    customGetter?: () => Date,
    customSetter?: (value: Date) => Promise<void>,
    propertyViewModelType?: ClassConstructor<TDateTimePropertyViewModel>,
    additionalCustomValidation?: () => BaseErrorInterface[],
    additionalObserverForValuePropertyChange?: Observable<any>
  ): TDateTimePropertyViewModel;
  /**
   * Restituisce un PropertyViewModel DateTime Non Nullable
   */
  getDateTimePropertyViewModel<TDateTimePropertyViewModel extends DateTimePropertyViewModel = DateTimePropertyViewModel>(
    action: GetGenericPropertyViewModelInterface<Date, TDateTimePropertyViewModel> | ((value: TDateTimePropertyViewModel) => void),
    value: TDateTimePropertyViewModel | undefined = undefined,
    propertyName: string = '',
    customGetter: undefined | (() => Date) = undefined,
    customSetter: undefined | ((value: Date) => Promise<void>) = undefined,
    propertyViewModelType: undefined | ClassConstructor<TDateTimePropertyViewModel> = undefined,
    additionalCustomValidation: undefined | (() => BaseErrorInterface[]) = undefined,
    additionalObserverForValuePropertyChange: undefined | Observable<any> = undefined,
  ): TDateTimePropertyViewModel {
    return this.getGenericPropertyViewModel<Date, TDateTimePropertyViewModel, DateTimeMetaDataInterface>({
      action,
      value,
      propertyName,
      customGetter,
      customSetter,
      propertyViewModelType: (propertyViewModelType ?? DateTimePropertyViewModel) as ClassConstructor<TDateTimePropertyViewModel>,
      additionalCustomValidation,
      additionalObserverForValuePropertyChange,
      metaDataList: this.domainModelMetaData.dateTimes
    });
  }
  //#endregion getDateTimePropertyViewModel

  //#region getNDateTimePropertyViewModel
  getNDateTimePropertyViewModel<TNDateTimePropertyViewModel extends NDateTimePropertyViewModel = NDateTimePropertyViewModel>(
    params: GetGenericPropertyViewModelInterface<Date, TNDateTimePropertyViewModel>
  ): TNDateTimePropertyViewModel;
  /**
   * @deprecated utilizza getNDateTimePropertyViewModel con in input l'object params e non l'action
   */
  getNDateTimePropertyViewModel<TNDateTimePropertyViewModel extends NDateTimePropertyViewModel = NDateTimePropertyViewModel>(
    action: (value: TNDateTimePropertyViewModel) => void,
    value: TNDateTimePropertyViewModel,
    propertyName: string,
    customGetter?: () => Date,
    customSetter?: (value: Date) => Promise<void>,
    propertyViewModelType?: ClassConstructor<TNDateTimePropertyViewModel>,
    additionalCustomValidation?: () => BaseErrorInterface[],
    additionalObserverForValuePropertyChange?: Observable<any>
  ): TNDateTimePropertyViewModel;
  /**
   * Restituisce un PropertyViewModel DateTime Nullable
   */
  getNDateTimePropertyViewModel<TNDateTimePropertyViewModel extends NDateTimePropertyViewModel = NDateTimePropertyViewModel>(
    action: GetGenericPropertyViewModelInterface<Date, TNDateTimePropertyViewModel> | ((value: TNDateTimePropertyViewModel) => void),
    value: TNDateTimePropertyViewModel | undefined = undefined,
    propertyName: string = '',
    customGetter: undefined | (() => Date) = undefined,
    customSetter: undefined | ((value: Date) => Promise<void>) = undefined,
    propertyViewModelType: undefined | ClassConstructor<TNDateTimePropertyViewModel> = undefined,
    additionalCustomValidation: undefined | (() => BaseErrorInterface[]) = undefined,
    additionalObserverForValuePropertyChange: undefined | Observable<any> = undefined,
  ): TNDateTimePropertyViewModel {
    return this.getGenericPropertyViewModel<Date, TNDateTimePropertyViewModel, DateTimeMetaDataInterface>({
      action,
      value,
      propertyName,
      customGetter,
      customSetter,
      propertyViewModelType: (propertyViewModelType ?? NDateTimePropertyViewModel) as ClassConstructor<TNDateTimePropertyViewModel>,
      additionalCustomValidation,
      additionalObserverForValuePropertyChange,
      metaDataList: this.domainModelMetaData.dateTimes
    });
  }
  //#endregion getNDateTimePropertyViewModel

  //#region getEnumPropertyViewModel
  getEnumPropertyViewModel<TEnumPropertyViewModel extends EnumPropertyViewModel = EnumPropertyViewModel>(
    params: GetGenericPropertyViewModelInterface<number, TEnumPropertyViewModel>
  ): TEnumPropertyViewModel;
  /**
   * @deprecated utilizza getEnumPropertyViewModel con in input l'object params e non l'action
   */
  getEnumPropertyViewModel<TEnumPropertyViewModel extends EnumPropertyViewModel = EnumPropertyViewModel>(
    action: (value: EnumPropertyViewModel) => void,
    value: EnumPropertyViewModel,
    propertyName: string,
    customGetter?: () => number,
    customSetter?: (value: number) => Promise<void>,
    propertyViewModelType?: ClassConstructor<EnumPropertyViewModel>,
    additionalCustomValidation?: () => BaseErrorInterface[],
    additionalObserverForValuePropertyChange?: Observable<any>
  ): TEnumPropertyViewModel;
  /**
   * Restituisce un PropertyViewModel Enum Non Nullable
   */
  getEnumPropertyViewModel<TEnumPropertyViewModel extends EnumPropertyViewModel = EnumPropertyViewModel>(
    action: GetGenericPropertyViewModelInterface<number, TEnumPropertyViewModel> | ((value: TEnumPropertyViewModel) => void),
    value: TEnumPropertyViewModel | undefined = undefined,
    propertyName: string = '',
    customGetter: undefined | (() => number) = undefined,
    customSetter: undefined | ((value: number) => Promise<void>) = undefined,
    propertyViewModelType: undefined | ClassConstructor<TEnumPropertyViewModel> = undefined,
    additionalCustomValidation: undefined | (() => BaseErrorInterface[]) = undefined,
    additionalObserverForValuePropertyChange: undefined | Observable<any> = undefined,
  ): TEnumPropertyViewModel {
    return this.getGenericPropertyViewModel<number, TEnumPropertyViewModel, EnumMetaDataInterface>({
      action,
      value,
      propertyName,
      customGetter,
      customSetter,
      propertyViewModelType: (propertyViewModelType ?? EnumPropertyViewModel) as ClassConstructor<TEnumPropertyViewModel>,
      additionalCustomValidation,
      additionalObserverForValuePropertyChange,
      metaDataList: this.domainModelMetaData.enums
    });
  }
  //#endregion getEnumPropertyViewModel

  //#region getNEnumPropertyViewModel
  getNEnumPropertyViewModel<TNEnumPropertyViewModel extends NEnumPropertyViewModel = NEnumPropertyViewModel>(
    params: GetGenericPropertyViewModelInterface<number, TNEnumPropertyViewModel>
  ): TNEnumPropertyViewModel;
  /**
   * @deprecated utilizza getNEnumPropertyViewModel con in input l'object params e non l'action
   */
  getNEnumPropertyViewModel<TNEnumPropertyViewModel extends NEnumPropertyViewModel = NEnumPropertyViewModel>(
    action: (value: NEnumPropertyViewModel) => void,
    value: NEnumPropertyViewModel,
    propertyName: string,
    customGetter?: () => number,
    customSetter?: (value: number) => Promise<void>,
    propertyViewModelType?: ClassConstructor<NEnumPropertyViewModel>,
    additionalCustomValidation?: () => BaseErrorInterface[],
    additionalObserverForValuePropertyChange?: Observable<any>
  ): TNEnumPropertyViewModel;
  /**
   * Restituisce un PropertyViewModel Enum Nullable
   */
  getNEnumPropertyViewModel<TNEnumPropertyViewModel extends NEnumPropertyViewModel = NEnumPropertyViewModel>(
    action: GetGenericPropertyViewModelInterface<number, TNEnumPropertyViewModel> | ((value: TNEnumPropertyViewModel) => void),
    value: TNEnumPropertyViewModel | undefined = undefined,
    propertyName: string = '',
    customGetter: undefined | (() => number) = undefined,
    customSetter: undefined | ((value: number) => Promise<void>) = undefined,
    propertyViewModelType: undefined | ClassConstructor<TNEnumPropertyViewModel> = undefined,
    additionalCustomValidation: undefined | (() => BaseErrorInterface[]) = undefined,
    additionalObserverForValuePropertyChange: undefined | Observable<any> = undefined,
  ): TNEnumPropertyViewModel {
    return this.getGenericPropertyViewModel<number, TNEnumPropertyViewModel, EnumMetaDataInterface>({
      action,
      value,
      propertyName,
      customGetter,
      customSetter,
      propertyViewModelType: (propertyViewModelType ?? NEnumPropertyViewModel) as ClassConstructor<TNEnumPropertyViewModel>,
      additionalCustomValidation,
      additionalObserverForValuePropertyChange,
      metaDataList: this.domainModelMetaData.enums
    });
  }
  //#endregion getNEnumPropertyViewModel

  //#region getBoolPropertyViewModel
  getBoolPropertyViewModel<TBoolPropertyViewModel extends BoolPropertyViewModel = BoolPropertyViewModel>(
    params: GetGenericPropertyViewModelInterface<boolean, TBoolPropertyViewModel>
  ): TBoolPropertyViewModel;
  /**
   * @deprecated utilizza getBoolPropertyViewModel con in input l'object params e non l'action
   */
  getBoolPropertyViewModel<TBoolPropertyViewModel extends BoolPropertyViewModel = BoolPropertyViewModel>(
    action: (value: TBoolPropertyViewModel) => void,
    value: TBoolPropertyViewModel,
    propertyName: string,
    customGetter?: () => boolean,
    customSetter?: (value: boolean) => Promise<void>,
    propertyViewModelType?: ClassConstructor<TBoolPropertyViewModel>,
    additionalCustomValidation?: () => BaseErrorInterface[],
    additionalObserverForValuePropertyChange?: Observable<any>
  ): TBoolPropertyViewModel;
  /**
   * Restituisce un PropertyViewModel Bool Non Nullable
   */
  getBoolPropertyViewModel<TBoolPropertyViewModel extends BoolPropertyViewModel = BoolPropertyViewModel>(
    action: GetGenericPropertyViewModelInterface<boolean, TBoolPropertyViewModel> | ((value: TBoolPropertyViewModel) => void),
    value: TBoolPropertyViewModel | undefined = undefined,
    propertyName: string = '',
    customGetter: undefined | (() => boolean) = undefined,
    customSetter: undefined | ((value: boolean) => Promise<void>) = undefined,
    propertyViewModelType: undefined | ClassConstructor<TBoolPropertyViewModel> = undefined,
    additionalCustomValidation: undefined | (() => BaseErrorInterface[]) = undefined,
    additionalObserverForValuePropertyChange: undefined | Observable<any> = undefined,
  ): TBoolPropertyViewModel {
    return this.getGenericPropertyViewModel<boolean, TBoolPropertyViewModel, BoolMetaDataInterface>({
      action,
      value,
      propertyName,
      customGetter,
      customSetter,
      propertyViewModelType: (propertyViewModelType ?? BoolPropertyViewModel) as ClassConstructor<TBoolPropertyViewModel>,
      additionalCustomValidation,
      additionalObserverForValuePropertyChange,
      metaDataList: this.domainModelMetaData.bools
    });
  }
  //#endregion getBoolPropertyViewModel

  //#region getNBoolPropertyViewModel
  getNBoolPropertyViewModel<TNBoolPropertyViewModel extends NBoolPropertyViewModel = NBoolPropertyViewModel>(
    params: GetGenericPropertyViewModelInterface<boolean, TNBoolPropertyViewModel>
  ): TNBoolPropertyViewModel;
  /**
   * @deprecated utilizza getNBoolPropertyViewModel con in input l'object params e non l'action
   */
  getNBoolPropertyViewModel<TNBoolPropertyViewModel extends NBoolPropertyViewModel = NBoolPropertyViewModel>(
    action: (value: TNBoolPropertyViewModel) => void,
    value: TNBoolPropertyViewModel,
    propertyName: string,
    customGetter?: () => boolean,
    customSetter?: (value: boolean) => Promise<void>,
    propertyViewModelType?: ClassConstructor<TNBoolPropertyViewModel>,
    additionalCustomValidation?: () => BaseErrorInterface[],
    additionalObserverForValuePropertyChange?: Observable<any>
  ): NBoolPropertyViewModel;
  /**
   * Restituisce un PropertyViewModel Bool Nullable
   */
  getNBoolPropertyViewModel<TNBoolPropertyViewModel extends NBoolPropertyViewModel = NBoolPropertyViewModel>(
    action: GetGenericPropertyViewModelInterface<boolean, TNBoolPropertyViewModel> | ((value: TNBoolPropertyViewModel) => void),
    value: TNBoolPropertyViewModel | undefined = undefined,
    propertyName: string = '',
    customGetter: undefined | (() => boolean) = undefined,
    customSetter: undefined | ((value: boolean) => Promise<void>) = undefined,
    propertyViewModelType: undefined | ClassConstructor<TNBoolPropertyViewModel> = undefined,
    additionalCustomValidation: undefined | (() => BaseErrorInterface[]) = undefined,
    additionalObserverForValuePropertyChange: undefined | Observable<any> = undefined,
  ): TNBoolPropertyViewModel {
    return this.getGenericPropertyViewModel<boolean, TNBoolPropertyViewModel, BoolMetaDataInterface>({
      action,
      value,
      propertyName,
      customGetter,
      customSetter,
      propertyViewModelType: (propertyViewModelType ?? NBoolPropertyViewModel) as ClassConstructor<TNBoolPropertyViewModel>,
      additionalCustomValidation,
      additionalObserverForValuePropertyChange,
      metaDataList: this.domainModelMetaData.bools
    });
  }
  //#endregion getNBoolPropertyViewModel

  //#region getNumericPropertyViewModel
  getNumericPropertyViewModel<TModelType = BigNumber | number, TNumericPropertyViewModel extends NumericPropertyViewModelInterface<TModelType> = NumericPropertyViewModel<TModelType>>(
    params: GetGenericPropertyViewModelInterface<TModelType, TNumericPropertyViewModel>
  ): TNumericPropertyViewModel;
  /**
   * @deprecated utilizza getNumericPropertyViewModel con in input l'object params e non l'action
   */
  getNumericPropertyViewModel<TModelType = BigNumber | number, TNumericPropertyViewModel extends NumericPropertyViewModelInterface<TModelType> = NumericPropertyViewModel<TModelType>>(
    action: (value: TNumericPropertyViewModel) => void,
    value: TNumericPropertyViewModel,
    propertyName: string,
    customGetter?: (() => TModelType),
    customSetter?: ((value: TModelType) => Promise<void>),
    propertyViewModelType?: ClassConstructor<TNumericPropertyViewModel>,
    additionalCustomValidation?: (() => BaseErrorInterface[]),
    additionalObserverForValuePropertyChange?: Observable<any>
  ): TNumericPropertyViewModel;
  /**
   * Restituisce un PropertyViewModel Numerico Non Nullable
   */
  getNumericPropertyViewModel<TModelType = BigNumber | number, TNumericPropertyViewModel extends NumericPropertyViewModelInterface<TModelType> = NumericPropertyViewModel<TModelType>>(
    action: GetGenericPropertyViewModelInterface<TModelType, TNumericPropertyViewModel> | ((value: TNumericPropertyViewModel) => void),
    value: TNumericPropertyViewModel | undefined = undefined,
    propertyName: string = '',
    customGetter: undefined | (() => TModelType) = undefined,
    customSetter: undefined | ((value: TModelType) => Promise<void>) = undefined,
    propertyViewModelType: undefined | ClassConstructor<TNumericPropertyViewModel> = undefined,
    additionalCustomValidation: undefined | (() => BaseErrorInterface[]) = undefined,
    additionalObserverForValuePropertyChange: undefined | Observable<any> = undefined
  ): TNumericPropertyViewModel {

    return this.getGenericPropertyViewModel<TModelType, TNumericPropertyViewModel, NumericMetaDataInterface>({
      action,
      value,
      propertyName,
      customGetter,
      customSetter,
      propertyViewModelType: (propertyViewModelType ?? NumericPropertyViewModel) as ClassConstructor<TNumericPropertyViewModel>,
      additionalCustomValidation,
      additionalObserverForValuePropertyChange,
      metaDataList: this.domainModelMetaData.numerics
    });
  }
  //#endregion getNumericPropertyViewModel

  //#region getNNumericPropertyViewModel
  getNNumericPropertyViewModel<TModelType = BigNumber | number, TNNumericPropertyViewModel extends NNumericPropertyViewModel<TModelType> = NNumericPropertyViewModel<TModelType>>(
    params: GetGenericPropertyViewModelInterface<TModelType, TNNumericPropertyViewModel>
  ): TNNumericPropertyViewModel;
  /**
   * @deprecated utilizza getNNumericPropertyViewModel con in input l'object params e non l'action
   */
  getNNumericPropertyViewModel<TModelType = BigNumber | number, TNNumericPropertyViewModel extends NNumericPropertyViewModel<TModelType> = NNumericPropertyViewModel<TModelType>>(
    action: ((value: TNNumericPropertyViewModel) => void),
    value: TNNumericPropertyViewModel | undefined,
    propertyName: string,
    customGetter?: (() => TModelType),
    customSetter?: ((value: TModelType) => Promise<void>),
    propertyViewModelType?: ClassConstructor<TNNumericPropertyViewModel>,
    additionalCustomValidation?: (() => BaseErrorInterface[]),
    additionalObserverForValuePropertyChange?: Observable<any>
  ): TNNumericPropertyViewModel;
  /**
   * Restituisce un PropertyViewModel Numerico Nullable
   */
  getNNumericPropertyViewModel<TModelType = BigNumber | number, TNNumericPropertyViewModel extends NNumericPropertyViewModel<TModelType> = NNumericPropertyViewModel<TModelType>>(
    action: GetGenericPropertyViewModelInterface<TModelType, TNNumericPropertyViewModel> | ((value: TNNumericPropertyViewModel) => void),
    value: TNNumericPropertyViewModel | undefined = undefined,
    propertyName: string = '',
    customGetter: undefined | (() => TModelType) = undefined,
    customSetter: undefined | ((value: TModelType) => Promise<void>) = undefined,
    propertyViewModelType: undefined | ClassConstructor<TNNumericPropertyViewModel> = undefined,
    additionalCustomValidation: undefined | (() => BaseErrorInterface[]) = undefined,
    additionalObserverForValuePropertyChange: undefined | Observable<any> = undefined
  ): TNNumericPropertyViewModel {

    return this.getGenericPropertyViewModel<TModelType, TNNumericPropertyViewModel, NumericMetaDataInterface>({
      action,
      value,
      propertyName,
      customGetter,
      customSetter,
      propertyViewModelType: (propertyViewModelType ?? NNumericPropertyViewModel) as ClassConstructor<TNNumericPropertyViewModel>,
      additionalCustomValidation,
      additionalObserverForValuePropertyChange,
      metaDataList: this.domainModelMetaData.numerics
    });
  }
  //#endregion getNNumericPropertyViewModel

  //#region getPropertyViewModel
  getPropertyViewModel<T extends PropertyViewModelInterface<TValue>, TValue>(
    params: GetPropertyViewModelInterface<T, TValue>
  ): T;
  /**
   * @deprecated utilizza getPropertyViewModel con in input l'object params e non l'action
   */
  getPropertyViewModel<T extends PropertyViewModelInterface<TValue>, TValue>(
    action: (value: T) => void,
    value: T,
    propertyName: string,
    propertyViewModelType: ClassConstructor<PropertyViewModelInterface>,
    metaDataList: PropertyMetaDataInterface[],
    customGetter?: (() => TValue),
    customSetter?: ((value: TValue) => Promise<void>),
  ): T;
  /**
   * Ritorna un PropertyViewModel
   */
  getPropertyViewModel<T extends PropertyViewModelInterface<TValue>, TValue>(
    actionOrOpts: GetPropertyViewModelInterface<T, TValue> | ((value: T) => void),
    value: T | undefined = undefined,
    propertyName: string = '',
    propertyViewModelType: ClassConstructor<T> | undefined = undefined,
    metaDataList: PropertyMetaDataInterface[] | undefined = undefined,
    customGetter: undefined | (() => TValue) = undefined,
    customSetter: undefined | ((value: TValue) => Promise<void>) = undefined,
  ): T {

    // Destructuring sicuro all'inizio con type narrowing
    let finalAction: (value: T) => void;
    let finalValue = value;
    let finalPropertyName = propertyName;
    let finalPropertyViewModelType = propertyViewModelType;
    let finalMetaDataList = metaDataList;
    let finalCustomGetter = customGetter;
    let finalCustomSetter = customSetter;

    if (typeof actionOrOpts !== 'function') {
      const opts = actionOrOpts;
      finalAction = opts.action;
      finalValue = opts.value ?? value;
      finalPropertyName = opts.propertyName ?? propertyName;
      finalPropertyViewModelType = opts.propertyViewModelType ?? propertyViewModelType;
      finalMetaDataList = opts.metaDataList ?? metaDataList;
      finalCustomGetter = opts.customGetter ?? customGetter;
      finalCustomSetter = opts.customSetter ?? customSetter;
    } else {
      finalAction = actionOrOpts;
    }

    if (finalValue == null) {
      finalValue = this.createPropertyViewModel<T, TValue>(
        finalPropertyName,
        finalPropertyViewModelType,
        finalMetaDataList,
        finalCustomGetter,
        finalCustomSetter
      );
    }
    finalAction(finalValue);
    return finalValue;
  }
  //#endregion getPropertyViewModel

  protected getGenericPropertyViewModel<
    TValue, TPropertyViewModel extends PropertyViewModelInterface<TValue>,
    TMetaDataList extends PropertyMetaDataInterface = PropertyMetaDataInterface
  >({
    action,
    value,
    propertyName,
    customGetter,
    customSetter,
    propertyViewModelType,
    additionalCustomValidation,
    additionalObserverForValuePropertyChange,
    metaDataList
  }: {
    action: GetGenericPropertyViewModelInterface<TValue, TPropertyViewModel> | ((value: TPropertyViewModel) => void),
    value: TPropertyViewModel | undefined,
    propertyName: string,
    customGetter: undefined | (() => TValue),
    customSetter: undefined | ((value: TValue) => Promise<void>),
    propertyViewModelType: undefined | ClassConstructor<TPropertyViewModel>,
    additionalCustomValidation: undefined | (() => BaseErrorInterface[]),
    additionalObserverForValuePropertyChange: undefined | Observable<any>,
    metaDataList: TMetaDataList[];
  }) {

    // Destructuring sicuro all'inizio con type narrowing
    let finalAction: (value: TPropertyViewModel) => void;
    let finalValue = value;
    let finalPropertyName = propertyName;
    let finalCustomGetter = customGetter;
    let finalCustomSetter = customSetter;
    let finalPropertyViewModelType = propertyViewModelType;
    let finalAdditionalCustomValidation = additionalCustomValidation;
    let finalAdditionalObserverForValuePropertyChange = additionalObserverForValuePropertyChange;
    const finalMetaDataList = metaDataList;

    if (typeof action !== 'function') {
      const opts = action;
      finalAction = opts.action;
      finalValue = opts.value ?? value;
      finalPropertyName = opts.propertyName ?? propertyName;
      finalCustomGetter = opts.customGetter ?? customGetter;
      finalCustomSetter = opts.customSetter ?? customSetter;
      finalPropertyViewModelType = opts.propertyViewModelType ?? propertyViewModelType;
      finalAdditionalCustomValidation = opts.additionalCustomValidation ?? additionalCustomValidation;
      finalAdditionalObserverForValuePropertyChange = opts.additionalObserverForValuePropertyChange ?? additionalObserverForValuePropertyChange;
    } else {
      finalAction = action;
    }

    const pvm = this.getPropertyViewModel<TPropertyViewModel, TValue>({
      action: finalAction,
      value: finalValue,
      propertyName: finalPropertyName,
      propertyViewModelType: finalPropertyViewModelType,
      metaDataList: finalMetaDataList,
      customGetter: finalCustomGetter,
      customSetter: finalCustomSetter,
    });

    if (finalAdditionalCustomValidation) {
      pvm.additionalCustomValidation = () => finalAdditionalCustomValidation();
    }

    if (finalAdditionalObserverForValuePropertyChange) {
      pvm.setAdditionalObserverForValuePropertyChange(finalAdditionalObserverForValuePropertyChange);
    }

    return pvm;
  }

  protected createPropertyViewModel<TPropertyViewModel extends PropertyViewModelInterface<TValue>, TValue>(
    propertyName: string,
    propertyViewModelType: ClassConstructor<PropertyViewModelInterface>,
    metaDataList: PropertyMetaDataInterface[],
    customGetter?: (() => TValue),
    customSetter?: ((value: TValue) => Promise<void>),
  ): TPropertyViewModel {
    if (!this.behindPropertyViewModels.has(propertyName)) {
      const metaData = metaDataList.find(item => MetaDataUtils.toCamelCase(item.name) === propertyName);
      const isCustom = CustomPropertyViewModelInspector.isApplied(this, propertyName);
      if (!metaData && !isCustom) {
        LogService.warn(
          `MetaData non trovato per la property '${propertyName}' di tipo ${propertyViewModelType.prototype.constructor.name} nel view model ${this.constructor.name}`,
          { propertyName, propertyViewModelType, metaDataList }
        );
      }
      const initializationInfo = this.createPVMInitializationInfo(propertyName, metaData, this, undefined, undefined, customGetter, customSetter);
      const pvm = new propertyViewModelType(initializationInfo);

      this.setDefaultValueFromLayout(pvm);

      this.behindPropertyViewModels.set(propertyName, pvm);
    }
    return this.behindPropertyViewModels.get(propertyName) as TPropertyViewModel;
  }

  protected createPVMInitializationInfo<TPropertyMetaData extends PropertyMetaDataInterface>(
    propertyName: string,
    propertyMetaData: PropertyMetaDataInterface,
    parent: ViewModelInterface,
    setModel = true,
    setParent = true,
    customGetter?: (() => any),
    customSetter?: ((value: any) => Promise<void>),
  ): PropertyViewModelInitializationInfo {

    return PropertyViewModelFactory.createPVMInitializationInfo(
      this,
      propertyName,
      propertyMetaData,
      parent,
      this.modifiedSubscriber,
      this.eventDispatcher,
      setModel ? this.domainModel : null,
      setModel,
      setParent,
      [this.reservedPath, this.reservedName].filter((w) => w?.length > 0).join('.'),
      customGetter,
      customSetter
    );
  }

  setDefaultValueFromLayout(pvm: PropertyViewModelInterface): void {
    const path = pvm.propertyPath?.length > 0 ? (pvm.propertyPath + '.') : '';
    const fullPathName = (path + pvm.propertyName).split('.').filter((w) => w !== 'selectedItem').join('.');
    const defaultValue: string | number | BigNumber | boolean | Date | DateTimeOffset = this._layoutDefaultValueList?.get(fullPathName);
    if (defaultValue != null) {
      pvm.setDefaultValueFromLayoutMetaData(defaultValue);
    }
  }
}

// Spostata qua a causa di un problema di circular dependency
export class ExtensionDataViewModel<
  TIdentityInterface extends IdentityInterface = IdentityInterface,
  TModel extends ModelInterface<TIdentityInterface> = ModelInterface<TIdentityInterface>
> extends ViewModel<
  TModel,
  TIdentityInterface
> implements ExtensionDataViewModelInterface<
  TIdentityInterface,
  TModel
> {
  protected override internalParent: ViewModelInterface = null;

  override get parent() {
    return this.internalParent;
  }
  override set parent(value: ViewModelInterface) {
    this.internalParent = value;
  }

  constructor({
    domainModel,
    domainModelMetaData,
    modifiedSubscriber,
    parent,
    eventDispatcher,
    aggregateMetaData,
    reservedPath,
    reservedName
  }: ExtensionDataViewModelArguments<TIdentityInterface, TModel>) {
    super();
    this._modifiedSubscriber = modifiedSubscriber;
    this.domainModel = domainModel;
    this.domainModelMetaData = domainModelMetaData;
    this.parent = parent;
    this.eventDispatcher = eventDispatcher;
    this.aggregateMetaData = aggregateMetaData;
    this.reservedPath = reservedPath;
    this.reservedName = reservedName;
  }

  async buildPropertyViewModels(forceAllPropertiesAsExtension: boolean = false) {
    this.propertyViewModels.clear();

    if (this.domainModelMetaData == null) {
      LogService.warn(`ExtensionDataViewModel: Error creating property view models for ${this.domainModelMetaData?.propertyNames?.join(', ')}`);
      return;
    }

    // Recupera i metadati delle proprietà del domain model
    const dynamicPropertyNames = this.domainModelMetaData.propertyNames.filter((p) => {
      const metaData = this.domainModelMetaData.getPropertyMetaData(p);
      metaData.isExtension = metaData.isExtension || forceAllPropertiesAsExtension;
      return metaData != null && metaData.isExtension;
    });

    for (const propertyName of dynamicPropertyNames.map(p => MetaDataUtils.toCamelCase(p))) {
      try {
        const pvm = await this.getDynamicPropertyViewModel(propertyName);
        this.propertyViewModels.set(propertyName, pvm);
      } catch (error) {
        LogService.warn(`ExtensionDataViewModel: Error creating property view model for ${propertyName}`, error);
      }
    }
  }

  async getExtensionExternalDomainModelFromPlainObject(propertyName: string, externalMetaData: ExternalMetaDataInterface): Promise<DynamicExternal> {
    if (this.domainModel == null) {
      return null;
    }

    const currentRefValue = this.domainModel.getDynamicPropertyValue(propertyName);

    // se il ref è nullo e pure i suoi backing field sono nulli allora ritorno null altrimenti costruisco un modello dell'external valorizzato con i backing field
    if (currentRefValue == null && externalMetaData.associationProperties?.every(ap => {
      const backingFieldValue = this.domainModel.getDynamicPropertyValue(MetaDataUtils.toCamelCase(ap.principalPropertyName));
      return backingFieldValue == null || (typeof backingFieldValue === 'string' && backingFieldValue.trim() === '');
    })) {
      return null;
    }

    // creo l'identity dell'external con i valori dei backing field, se è valorizzato significa che devo costruire un modello dell'external altrimenti lo lascio null

    // devo chiamare la findvalues per popolare il ref
    if (currentRefValue == null) {

      const externalIdentity = new DynamicExternalIdentity();

      for (const associationProperty of externalMetaData.associationProperties ?? []) {
        const identityPropertyName = MetaDataUtils.toCamelCase(associationProperty.dependentPropertyName);
        const backingFieldPropertyName = MetaDataUtils.toCamelCase(associationProperty.principalPropertyName);
        const backingFieldValue = this.domainModel.getDynamicPropertyValue(backingFieldPropertyName);
        if (backingFieldValue != null) {
          externalIdentity.setPropertyValue(identityPropertyName, backingFieldValue);
        }
      }

      const ovm = this._modifiedSubscriber as CoreOrchestratorViewModelInterface;
      return await ovm.getDynamicExternal(externalIdentity, externalMetaData.dependentAggregateMetaData.rootMetaData);
    }

    // Non sta arrivando l'oggetto DynamicExternal, ma un object plain
    if (currentRefValue[DomainModelExtension.EXTENSION_DATA_PROPERTY_NAME]) {
      const extensionData = currentRefValue[DomainModelExtension.EXTENSION_DATA_PROPERTY_NAME];
      const dynamicExternal = new DynamicExternal();
      dynamicExternal[DomainModelExtension.EXTENSION_DATA_PROPERTY_NAME] = DomainModelExtension.fromPlainObject({});

      for (const key in extensionData) {
        if (extensionData.hasOwnProperty(key)) {
          if ('$id' === key) {
            continue; // Ignora la proprietà $id
          }
          const value = extensionData[key];
          dynamicExternal.setDynamicPropertyValue(MetaDataUtils.toCamelCase(key), value);
        }
      }

      return dynamicExternal;

    }

    // // TODO da implementare, il codice valorizza semplicemente i campi dell'identity senza i campi descrittivi da prendere dal currentRefValue
    // const dynamicExternal = new DynamicExternal();

    // // recupero i backing field valorizzati nel domain model che mi servono per costruire il modello dell'external, se è valorizzato significa che devo costruire un modello dell'external altrimenti lo lascio null
    // for (const associationProperty of externalMetaData.associationProperties ?? []) {
    //   const identityPropertyName = MetaDataUtils.toCamelCase(associationProperty.dependentPropertyName);
    //   const backingFieldPropertyName = MetaDataUtils.toCamelCase(associationProperty.principalPropertyName);
    //   const backingFieldValue = this.domainModel.getDynamicPropertyValue(backingFieldPropertyName);
    //   if (backingFieldValue != null) {
    //     dynamicExternal.setDynamicPropertyValue(identityPropertyName, backingFieldValue);
    //   }
    // }



    // if(plainExternalDomainModel) {
    //   console.log(plainExternalDomainModel);
    //   return new DynamicExternal();
    // } else {
    //   return null;
    // }
    return null;
  }

  async buildExternalViewModels(isMock: boolean = false) {

    for (const externalMetaData of this.domainModelMetaData.externals) {
      const propertyName = MetaDataUtils.toCamelCase(externalMetaData.principalPropertyName);

      if (externalMetaData.isExtension) {
        const { DynamicExternalViewModel } = await import('./external-view-model');

        // Creo il DecodeViewModel
        const externalViewModelType = DynamicExternalViewModel;
        const externalModelType = DynamicExternal;


        if (externalMetaData.cloneType === CloneTypes.LocalReplica && this.domainModel.getDynamicPropertyValue[propertyName] == null) {
          this.domainModel.emitChanges = false;
          this.domainModel[propertyName] = new externalModelType();
          this.domainModel.emitChanges = true;
        }

        const externalDomainModel: DynamicExternal = await this.getExtensionExternalDomainModelFromPlainObject(propertyName, externalMetaData);

        // const mergedDecoratorData = ExternalInspector.getValue(this.domainModelType, propertyName);
        const externalViewModel = await ViewModelFactory.createExternalViewModel(
          externalViewModelType,
          externalDomainModel,
          this.aggregateMetaData,
          this.modifiedSubscriber as CoreOrchestratorViewModelInterface,
          externalMetaData,
          [this.reservedPath, this.reservedName].filter((w) => w?.length > 0).join('.'),
          this.isMock,
          externalModelType,
          // mergedDecoratorData,
          // processedNulledModel
        );
        externalViewModel.parent = this.parent;
        this[propertyName] = externalViewModel;
        this.relationViewModels.set(propertyName, externalViewModel);

        this.parent.relationViewModels.set(MetaDataUtils.toCamelCase(externalMetaData.principalPropertyName), externalViewModel);
        externalViewModel.externalDomainModelChanged.subscribe((arg) => this.externalEntityChangedHandler(arg, externalViewModel));

        // TODO
        // var item = evm as ISupportHighlight;
        // if (item != null)
        // {
        // 	EntityPropertiesMap.Add(p.Name, item);
        // }

        // se il suo backing field è popolato ma il suo ref non lo è eseguo una setcodevalue che mi permette di popolare correttamente il suo ref
        if (isMock === false && externalMetaData.associationProperties?.length === 1) {
          const backingFieldPropertyName = MetaDataUtils.toCamelCase(externalMetaData.associationProperties[0].principalPropertyName);
          const backingField = this.parent.getProperty(backingFieldPropertyName);
          const extVm = this[propertyName] as ExternalViewModelInterface;

          if (extVm.checkAndFixNullExternal) {
            const extFieldPropertyName = MetaDataUtils.toCamelCase(externalMetaData.associationProperties[0].dependentPropertyName);
            const extField = extVm?.getProperty(extFieldPropertyName);

            if (
              // se il backing field è diverso da null
              backingField?.value != null &&
              (
                // il suo external è nullo
                extField?.value == null ||  //o
                // è diverso dal suo backing field
                backingField?.value != extField?.value
              )) {
              // Se l'identity è valida
              if (extVm.identityIsValid({ [backingField.propertyMetaData.name]: backingField.value })) {
                const oldValue1 = extVm.backingFieldCanNotifyModified;
                const oldValue2 = extField.canNotifyModified;
                extVm.backingFieldCanNotifyModified = false;
                extField.canNotifyModified = false;
                await extVm.setCodeValue(backingField?.value);
                extVm.backingFieldCanNotifyModified = oldValue1;
                extField.canNotifyModified = oldValue2;

              }

            }
          }


        }

      }
    }

  }

  async buildInternalViewModels(
    isMock: boolean = false,
  ) {
    for (const relationMetadata of this.domainModelMetaData.internalRelations) {

      const propertyName = MetaDataUtils.toCamelCase(relationMetadata.principalPropertyName);

      if (relationMetadata.isExtension) {
        const internalModel: DynamicInternal = this.domainModel.getDynamicPropertyValue(propertyName);
        if (internalModel != null) {
          const { DynamicInternalViewModel } = await import('./aggregate-element-view-model');

          // Recupero il tipo del modello dell'internal dal decoratore @Type
          let internalModelType = DynamicInternal;
          let internalViewModelType = DynamicInternalViewModel;

          const ivm = await ViewModelFactory.createInternalViewModel<
            DynamicInternalIdentity, DynamicInternal
          >({
            internalViewModelType,
            internalModel,
            aggregateMetaData: this.aggregateMetaData,
            orchestratorViewModel: this.modifiedSubscriber as CoreOrchestratorViewModelInterface,
            relationMetadata,
            skipPostInit: false,
            path: [this.reservedPath, this.reservedName].filter((w) => w?.length > 0).join('.'),
            isMock,
            parent: this,
            internalModelType,
          });
          this[propertyName] = ivm;
          this.relationViewModels.set(propertyName, ivm);
          this.parent.relationViewModels.set(propertyName, ivm);
          ivm.internalDomainModelChanged.subscribe((arg) => this.internalEntityChangedHandler(arg, ivm));

          ivm.viewModelChanged.subscribe(name => {
            this.parent.viewModelChangeDebouncer.next(name);
          });
        }
      }
    }
  }

  protected internalEntityChangedHandler(arg: ModelChangedEventArgsInterface<ModelInterface>, internalViewModel: InternalViewModelInterface): void {
    if (this.domainModel) {
      const name = arg.senderName;

      this.domainModel.setDynamicPropertyValue(name, arg.model);

      internalViewModel.validate();

      this.parent.viewModelChangeDebouncer.next(name);
    }
  }

  protected externalEntityChangedHandler(arg: ExternalDomainModelChangedEventArgsInterface, externalViewModel: ExternalViewModelInterface): void {
    if (this.domainModel) {
      const name = arg.senderName;
      // assegnamento del nuovo domain model esterno
      // this.domainModel[name] = arg.domainModel;
      this.domainModel.setDynamicPropertyValue(name, arg.domainModel);

      externalViewModel.validate();

      this.parent.viewModelChangeDebouncer.next(name);
    }
  }

  private async getDynamicPropertyViewModel(
    propertyName: string
  ): Promise<PropertyViewModelInterface> {

    const propertyMetaData = this.domainModelMetaData.getPropertyMetaData(propertyName);
    if (propertyMetaData == null) {
      throw new Error(`ExtensionDataViewModel: Property '${propertyName}' not found in domain model.`);
    }

    let pvm: PropertyViewModelInterface;
    switch (propertyMetaData.getType()) {
      case StringMetaData.TYPE:
        pvm = await PropertyViewModelFactory.createDynamicPropertyViewModel({
          propertyName,
          propertyViewModelType: StringPropertyViewModel,
          propertyMetaData,
          parent: this.parent,
          modifiedSubscriber: this.modifiedSubscriber,
          eventDispatcher: this.eventDispatcher,
          model: this.domainModel,
          path: [this.reservedPath, this.reservedName].filter((w) => w?.length > 0).join('.'),
        });
        break;
      case NumericMetaData.TYPE:
        pvm = await PropertyViewModelFactory.createDynamicPropertyViewModel({
          propertyName,
          propertyViewModelType: propertyMetaData.isNullable ? NNumericPropertyViewModel : NumericPropertyViewModel,
          propertyMetaData,
          parent: this.parent,
          modifiedSubscriber: this.modifiedSubscriber,
          eventDispatcher: this.eventDispatcher,
          model: this.domainModel,
          path: [this.reservedPath, this.reservedName].filter((w) => w?.length > 0).join('.'),
        });
        break;
      case BoolMetaData.TYPE:
        pvm = await PropertyViewModelFactory.createDynamicPropertyViewModel({
          propertyName,
          propertyViewModelType: propertyMetaData.isNullable ? NBoolPropertyViewModel : BoolPropertyViewModel,
          propertyMetaData,
          parent: this.parent,
          modifiedSubscriber: this.modifiedSubscriber,
          eventDispatcher: this.eventDispatcher,
          model: this.domainModel,
          path: [this.reservedPath, this.reservedName].filter((w) => w?.length > 0).join('.'),
        });
        break;
      case EnumMetaData.TYPE:
        pvm = await PropertyViewModelFactory.createDynamicPropertyViewModel({
          propertyName,
          propertyViewModelType: propertyMetaData.isNullable ? NEnumPropertyViewModel : EnumPropertyViewModel,
          propertyMetaData,
          parent: this.parent,
          modifiedSubscriber: this.modifiedSubscriber,
          eventDispatcher: this.eventDispatcher,
          model: this.domainModel,
          path: [this.reservedPath, this.reservedName].filter((w) => w?.length > 0).join('.'),
        });
        break;
      case DateTimeMetaData.TYPE:
        const metaData = propertyMetaData as DateTimeMetaDataInterface;

        const propertyViewModelType = metaData.isDateTimeOffset
          ? (propertyMetaData.isNullable ? NDateTimeOffsetPropertyViewModel : DateTimeOffsetPropertyViewModel)
          : (propertyMetaData.isNullable ? NDateTimePropertyViewModel : DateTimePropertyViewModel);

        pvm = await PropertyViewModelFactory.createDynamicPropertyViewModel<PropertyViewModelInterface, DateTimeMetaDataInterface>({
          propertyName,
          propertyViewModelType,
          propertyMetaData: metaData,
          parent: this.parent,
          modifiedSubscriber: this.modifiedSubscriber,
          eventDispatcher: this.eventDispatcher,
          model: this.domainModel,
          path: [this.reservedPath, this.reservedName].filter((w) => w?.length > 0).join('.'),
        });
        break;
      case ModelVersionMetaData.TYPE:
        pvm = await PropertyViewModelFactory.createDynamicPropertyViewModel({
          propertyName,
          propertyViewModelType: VersionPropertyViewModel,
          propertyMetaData,
          parent: this.parent,
          modifiedSubscriber: this.modifiedSubscriber,
          eventDispatcher: this.eventDispatcher,
          model: this.domainModel,
          path: [this.reservedPath, this.reservedName].filter((w) => w?.length > 0).join('.'),
        });
        break;
      default:
        throw new Error(`ExtensionDataViewModel: Unsupported property type '${propertyMetaData.getType()}'.`);
    }

    this.parent?.setDefaultValueFromLayout(pvm);
    return pvm;
  }
}
